import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, Target, Copy, Users, Trash2, Save, Search, Info } from 'lucide-react';
import { kpiApi, masterApi, ALL_UNITS } from '../lib/api';
import { useUnit } from '../contexts/UnitContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import BandLadder from '../components/BandLadder';
import AccidentLadders from '../components/AccidentLadders';
import KpiMasterPanel, { KpiIcon, kpiLook } from '../components/KpiMasterPanel';
import MandatoryKpis from '../components/MandatoryKpis';
// CriteriaTab and CategoriesTab are not imported: their tabs were removed, and
// an unused import is a build warning on every compile. The files are still
// there and so is everything behind them.


/**
 * A ladder as rows for display: the condition in words, and the marks.
 *
 * Deliberately NOT a scoring function — it renders a ladder that is already
 * stored. First-match-wins means a rung is bounded by the one above it, which
 * is why "up to 1000" reads as "500 to 1000" when a "up to 500" rung precedes
 * it. The server says the same thing in `_band_labels`; this is the display
 * side of it and never decides a mark.
 */
function bandRows(bands) {
  const out = [];
  let prevGte = null;
  let prevLte = null;
  const fmt = (x) => (Number(x) === Math.trunc(Number(x)) ? String(Math.trunc(Number(x))) : String(x));
  for (const b of bands || []) {
    let condition;
    if ('gte' in b) {
      const t = fmt(b.gte);
      condition = prevGte !== null ? `${t} to ${prevGte}` : `${t} and above`;
      prevGte = t;
    } else if ('lte' in b) {
      const t = fmt(b.lte);
      condition = prevLte !== null ? `${prevLte} to ${t}` : `Up to ${t}`;
      prevLte = t;
    } else {
      condition = 'Anything else';
    }
    out.push({ condition, score: b.score });
  }
  return out;
}

/**
 * Breaks already-authored scoring text into tiers for display only.
 *
 * Splits ONLY right before a token that clearly starts a new tier (a number
 * next to "marks"/"=", or a comparison word) — never on a bare comma — so an
 * Indian-format number like "1,00,000" is never cut in half. When no such
 * split point exists the text is shown exactly as written, as one line.
 */
function splitCriteriaText(text) {
  if (!text) return [];
  const parts = text.split(/,\s*(?=\d+(?:\.\d+)?\s*marks?\b|more than|up to|below|above|at or|not held|less than|≤|<=|>=|≥)/i)
    .map((s) => s.trim()).filter(Boolean);
  return parts.length > 1 ? parts : [text.trim()];
}

/**
 * A share of 100% written the way a person would write it.
 *
 * `toFixed(1)` turns four equal KPIs into "25.0%", which reads as a measured
 * quantity when it is in fact exactly a quarter. Whole numbers lose the
 * decimal; the thirds and sixths that genuinely need one keep it.
 *
 * Display only — `normalised_kpi_weights` on the server is what actually
 * weights a KPI, and it is untouched by how this prints.
 */
function pct(n) {
  if (!Number.isFinite(n)) return '0%';
  const r = Math.round(n * 10) / 10;
  return `${Number.isInteger(r) ? r : r.toFixed(1)}%`;
}
import MetricPicker from '../components/MetricPicker';
import SourceData from './SourceData';
import { currentFy, lastCompletedPeriod } from '../lib/utils';

const prevPeriod = lastCompletedPeriod;

/**
 * The live reading of a source field, per plant, with the mark it earns.
 *
 * Everything is computed server-side by `resolve_master_kpi` — the same
 * function the monthly job runs — so what this shows IS what the KPI would
 * produce. It re-reads whenever the field, denominator or ladder changes, so
 * editing a rung updates the marks beside the figures immediately.
 *
 * DECLARED AT MODULE LEVEL, deliberately. This used to sit inside `KpiCenter`,
 * which makes it a NEW component type on every render of the page: React
 * cannot match the old element to the new type, so it unmounts and remounts
 * this subtree each time. The effect below debounces its read by 250ms and
 * cancels on unmount, so every remount threw away the answer in flight and
 * started another — the panel fired a request a second, discarded all of them,
 * and sat on "Reading the latest month…" forever. The ladder builder is
 * anchored on that reading, so choosing a direction could not build a ladder
 * either.
 *
 * Keep it here. `activeUnit` is a prop for the same reason: closing over the
 * page's state is what pulled the definition inside in the first place.
 */
function FieldPreview({ source, field, denominator, uom, bands, activeUnit, onReading }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const bandsKey = JSON.stringify(bands || []);
  useEffect(() => {
    if (!field) { setData(null); setErr(''); return; }
    let live = true;
    const src = source === 'operations' ? 'ops' : source === 'maintenance' ? 'maint' : source;
    const t = setTimeout(() => {
      kpiApi.previewFormula({ source: src, field, denominator_field: denominator || null,
                              uom: uom || '', bands: JSON.parse(bandsKey) })
        .then((r) => { if (live) { setData(r.data); setErr(''); } })
        .catch((e) => { if (live) { setData(null); setErr(e?.response?.data?.detail || 'Could not read this field'); } });
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [source, field, denominator, uom, bandsKey]);

  // Scoped to the plant selected in the header. The resolver returns every
  // unit it can score, which is right for "All Units" but wrong everywhere
  // else: reading Unit 1's figure while Unit 3 is selected is how the wrong
  // plant's number gets used to judge a marking ladder. On "All Units" the
  // full list IS the answer, so nothing is filtered there.
  const allRows = data?.rows || [];
  // Only PER-PLANT rows are scoped to the header's plant. An HR company figure
  // is one number for the business and an employee metric is one number per
  // person; filtering either by plant finds nothing and then announces
  // "nothing reported for Unit 3" about a figure that was never per-plant.
  const perUnit = !data?.scope || data.scope === 'unit';
  const scoped = perUnit && activeUnit && activeUnit !== ALL_UNITS
    ? allRows.filter((r) => r.unit === activeUnit)
    : allRows;
  // A unit the source does not report would otherwise show an empty table
  // with no explanation; fall back to the full list and say why.
  const previewRows = scoped.length ? scoped : allRows;
  const scopedOut = scoped.length ? allRows.length - scoped.length : 0;
  // Hand the figure back up, because the ladder builder is anchored on it —
  // the reading the source already publishes is the only number in this panel
  // that was not invented. Computed above the early returns below: a hook
  // after a conditional return is a hook that sometimes does not run.
  const hit = previewRows.find((r) => typeof r.value === 'number');
  const hitKey = hit ? `${hit.unit}|${hit.value}|${data?.period || ''}` : '';
  useEffect(() => {
    if (!onReading) return;
    onReading(hit ? { value: hit.value, unit: hit.unit, period: data?.period || '' } : null);
    // hitKey collapses the row to its identity; passing `hit` itself would be
    // a fresh object every render and re-fire this forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hitKey]);

  if (!field) return null;
  if (err) {
    return <div className="mt-2 mb-3 text-[11.5px] text-[#B14A0E] bg-[#FFF6E8]
                           border border-[#FBD9A8] rounded-lg px-3 py-2">{err}</div>;
  }
  if (!data) {
    return <div className="mt-2 mb-3 text-[11.5px] text-[#98A1B2]">Reading the latest month…</div>;
  }
  return (
    <div className="mt-2 mb-3 rounded-lg border border-[#DCE6FB] bg-white overflow-hidden"
      data-testid="kpi-field-preview">
      <div className="flex items-baseline gap-2 px-3 py-1.5 bg-[#F7FAFF] border-b border-[#EDF2FD] flex-wrap">
        <span className="text-[10px] uppercase tracking-wide text-[#8B8FA8]">What this reads now</span>
        <span className="text-[11px] text-[#5A6377]">{data.period}</span>
        {scopedOut > 0 && (
          <span className="text-[10px] font-semibold text-[#15307F] bg-[#E8EFFD] rounded px-1.5 py-0.5"
                title={`Showing the plant selected in the header. ${scopedOut} other plant(s) hidden — switch to All Units to see them.`}>
            {activeUnit} only
          </span>
        )}
        {scoped.length === 0 && allRows.length > 0 && activeUnit && activeUnit !== ALL_UNITS && (
          <span className="text-[10px] font-semibold text-[#B14A0E] bg-[#FFF6E8] rounded px-1.5 py-0.5">
            nothing reported for {activeUnit} — showing every plant
          </span>
        )}
        {data.month_to_date && (
          <span className="text-[10px] font-semibold text-[#B14A0E]">part month</span>
        )}
        <span className="ml-auto text-[10px] font-mono text-[#B4B9CC] truncate max-w-[280px]">
          {data.formula}
        </span>
      </div>
      <table className="w-full text-[12px]">
        <tbody>
          {previewRows.map((r) => (
            <tr key={r.unit} className="border-b border-[#F4F6FB] last:border-0">
              <td className="px-3 py-1.5 text-[#5A6377]">
                {r.unit}
                {/* The reason, in the row rather than hidden in a tooltip.
                    "No figure" invites the reading that the wiring is broken;
                    most of the time the source simply sent nothing for that
                    plant this month, and that is worth saying out loud. */}
                {(r.value === null || r.value === undefined) && r.reason && (
                  <div className="text-[10.5px] text-[#B4B9CC] leading-tight">{r.reason}</div>
                )}
                {/* And the working behind a figure that IS there. A bare 0 on
                    the Accident register reads as "nothing was found" when it
                    actually means a minor and a major wiped out all four
                    marks — the opposite of an empty register. */}
                {r.value !== null && r.value !== undefined && r.basis && (
                  <div className="text-[10.5px] text-[#8B8FA8] leading-tight">{r.basis}</div>
                )}
              </td>
              <td className="px-2 py-1.5 text-right tabular-nums font-medium text-[#11182B] w-28">
                {r.value === null || r.value === undefined
                  ? <span className="text-[#C9CFDC] font-normal">no figure</span>
                  : <>{r.value}{uom ? ` ${uom}` : ''}</>}
              </td>
              <td className="px-3 py-1.5 text-right w-24">
                {/* A missing mark is NOT a zero, and must never look like one. */}
                {r.marks === null || r.marks === undefined
                  ? <span className="text-[11px] text-[#B14A0E]" title={r.reason || 'no mark'}>
                      not scored
                    </span>
                  : <span className="font-semibold tabular-nums text-[#312E81]">
                      {r.marks} {Math.abs(Number(r.marks)) === 1 ? 'mark' : 'marks'}
                    </span>}
              </td>
            </tr>
          ))}
          {!(data.rows || []).length && (
            <tr><td className="px-3 py-2 text-[#98A1B2]">No plant reports this field yet.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// Sortable columns on the allocation table, in display order.
const ALLOC_COLS = [['name', 'Employee'], ['department', 'Dept'],
                    ['designation', 'Designation'], ['status', 'Status']];

/**
 * Targets a KPI is scored against, one per plant.
 *
 * Some KPIs cannot share a ladder across units. Hold material is the case that
 * forced this: a fixed "₹0 = 4 marks, ₹2 lakh = 0" ladder marks every plant
 * that holds any material at all as a failure, and separates a plant holding
 * ₹13 lakh from one holding ₹3 lakh only in that both score zero.
 *
 * So the plant gets a TARGET and the ladder follows from it. The target is the
 * only number anybody edits here — the five thresholds are derived, and are
 * shown underneath so the figure being typed is never abstract.
 */
function UnitTargets({ only }) {
  const [rows, setRows] = useState(null);
  const [draft, setDraft] = useState({});     // `${key}|${unit}` -> typed text
  const [saving, setSaving] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await kpiApi.unitTargets();
      const all = (r.data?.targets || []).filter((t) => (t.units || []).length > 0);
      // `only` narrows this to the KPI being looked at. The same component used
      // to list every target above the whole KPI list, which gave a setting
      // that applies to two KPIs a third of the page.
      setRows(only ? all.filter((t) => (t.scores_kpis || []).includes(only)) : all);
      setDraft({});
    } catch { setRows([]); }
  }, [only]);
  useEffect(() => { load(); }, [load]);

  const save = async (t) => {
    const targets = {};
    t.units.forEach((u) => {
      const typed = draft[`${t.key}|${u.unit}`];
      const v = typed === undefined ? u.target : Number(typed);
      if (Number.isFinite(v)) targets[u.unit] = v;
    });
    setSaving(t.key);
    try {
      await kpiApi.saveUnitTargets(t.key, { targets });
      toast.success(`${t.label} targets saved — marking ladders updated`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save the targets');
    } finally { setSaving(''); }
  };

  if (!rows || rows.length === 0) return null;

  return (
    <div className={only ? '' : 'mb-4 rounded-xl border border-[#E6E9F0] bg-[#FAFBFD] p-4'}
      data-testid="kpi-unit-targets">
      {!only && <h4 className="font-medium text-[#11182B] mb-1">Targets per plant</h4>}
      <p className="text-xs text-[#5A6377] mb-3">
        Scored against a figure set for each plant rather than one shared ladder.
        Set the target; the marking ladder is worked out from it.
      </p>
      {rows.map((t) => (
        <div key={t.key} className="mb-3 last:mb-0">
          <div className="flex items-baseline gap-2 flex-wrap mb-2">
            <span className="text-sm font-medium text-[#11182B]">{t.label}</span>
            <span className="text-[11px] text-[#98A1B2]">
              scores {t.scores_kpis.join(', ')} · on target = 4 marks, each {t.step_pct}% above costs one
            </span>
          </div>
          {t.help && <p className="text-[11px] text-[#98A1B2] mb-2">{t.help}</p>}
          <div className="flex flex-wrap gap-3">
            {t.units.map((u) => {
              const kk = `${t.key}|${u.unit}`;
              const val = draft[kk] !== undefined ? draft[kk] : String(u.target ?? '');
              return (
                <div key={u.unit} className="rounded-lg border border-[#E6E9F0] bg-white px-3 py-2">
                  <div className="text-[11px] text-[#5A6377] mb-1">{u.unit}</div>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-[#98A1B2]">{t.uom}</span>
                    <Input type="number" min="0" value={val}
                      data-testid={`unit-target-${t.key}-${u.unit}`}
                      onChange={(e) => setDraft({ ...draft, [kk]: e.target.value })}
                      className="w-32 text-sm" />
                  </div>
                  {/* The ladder this target produces. Shown because a target is
                      otherwise an abstraction — this is the thing it does. */}
                  <div className="mt-1 text-[10px] leading-snug text-[#98A1B2]">
                    {u.is_default && <span className="text-[#A16207]">default · </span>}
                    {u.criteria}
                  </div>
                </div>
              );
            })}
          </div>
          <Button size="sm" className="mt-2" onClick={() => save(t)} disabled={saving === t.key}
            data-testid={`unit-target-save-${t.key}`}>
            {saving === t.key ? 'Saving…' : 'Save targets'}
          </Button>
        </div>
      ))}
    </div>
  );
}

export default function KpiCenter() {
  const { activeUnit } = useUnit();
  const [tab, setTab] = useState('allocation');
  const fy = currentFy();

  // ---- templates ----
  const [templates, setTemplates] = useState([]);
  const [tmplHealth, setTmplHealth] = useState(null);
  const [sel, setSel] = useState('');
  const [items, setItems] = useState([]);
  const [tname, setTname] = useState('');
  const [busy, setBusy] = useState(false);

  // Department grouping for the sidebar comes from the backend, which derives
  // it from the employees actually on each sheet.
  const [tmplGroups, setTmplGroups] = useState([]);
  // The strip reads the KPI MASTER, which is what a template row is linked to.
  //
  // It used to call `compute-preview`, scoped to the selected unit and month.
  // That is the wrong question on this screen: a template is global — "same
  // for every employee on it, every unit" — so a unit's September file has no
  // bearing on it. Asking anyway produced "not used in this unit" and
  // "nothing in the file" under rows whose mapping is perfectly good.
  //
  // `masterFlat` is already loaded here for the KPI picker, so this costs no
  // extra request and cannot disagree with the KPI Master screen.
  const [tq, setTq] = useState('');          // template search ("plant head", "accounts"…)
  // Sheets named for a role the org chart no longer has.
  //
  // A template is named for a (department · designation) pair, and the org
  // chart is derived live from EMPLOYEES and open vacancies — never from the
  // designations master. So a sheet outlives its role the moment the last
  // person holding that designation is moved or leaves: the role stops
  // existing, the template does not, and it keeps appearing in every list as
  // though somebody could still be put on it.
  //
  // Hidden by default so the list shows only roles that exist, which is what
  // the Employees screen shows. Not deleted — a template is a decision, and
  // sheets taken off it may still carry recorded marks — so the toggle below
  // brings them back.
  const [showDeadRoles, setShowDeadRoles] = useState(false);
  const deadRoles = React.useMemo(
    // `=== false` strictly: a response from before this field existed leaves it
    // undefined, and must not make every template vanish.
    () => new Set((templates || []).filter((t) => t.role_exists === false)
      .map((t) => t.name)), [templates]);
  // The roles the org chart actually has. A new sheet is chosen from these
  // rather than typed, so a sheet can never be named after a role that does
  // not exist. `template_name` is built SERVER-side by `_org_template_name`,
  // because rebuilding its casing rules here is how the two come to disagree.
  const [orgRoles, setOrgRoles] = useState(null);
  const [tmplInfoOpen, setTmplInfoOpen] = useState(false);

  const loadTemplates = useCallback(async () => {
    try {
      const r = await kpiApi.templates(); setTemplates(r.data || []);
      const g = await kpiApi.templatesGrouped(); setTmplGroups(g.data?.groups || []);
      // Surfaced beside the list rather than left in a log: two active sheets
      // for one role look identical to two legitimate sheets.
      kpiApi.templateDiagnostics().then((d) => setTmplHealth(d.data)).catch(() => setTmplHealth(null));
    } catch (e) { toast.error('Failed to load templates'); }
    try {
      const o = await kpiApi.orgRoles(); setOrgRoles(o.data?.roles || []);
    } catch { setOrgRoles([]); }   // the picker degrades to empty, never to free text
  }, []);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  // The duplicate-sheet banner is a HEALTH CHECK, not a one-off notice.
  // It used to load only with the template list — on mount and after an edit
  // made here — and the tab lives in component state, so opening Templates
  // never remounted anything. A duplicate resolved on the server (the boot
  // alignment retires them by itself) stayed on screen until somebody
  // happened to hard-reload, which is how a warning about five duplicate
  // sheets outlived the duplicates. Re-asking on each visit costs one cheap
  // GET and lets a fixed problem clear itself.
  useEffect(() => {
    if (tab !== 'templates') return;
    kpiApi.templateDiagnostics()
      .then((d) => setTmplHealth(d.data))
      .catch(() => { /* a failed health check must never blank the list */ });
  }, [tab]);

  // Run the merge the banner describes, then reload the list and re-check.
  // A preview first, so a warning that is merely STALE costs nothing — if the
  // server already agrees there is nothing to merge, the banner just clears.
  const reconcileTemplates = useCallback(async () => {
    setBusy(true);
    try {
      const pre = await kpiApi.reconcileTemplates(true);
      const n = pre.data?.duplicate_identities || 0;
      if (n) {
        const done = await kpiApi.reconcileTemplates(false);
        toast.success(`Merged ${done.data?.duplicate_identities || 0} duplicate sheet(s). `
          + 'Nothing was deleted — the retired ones are kept.');
      } else {
        toast.success('Already one sheet per role — the warning was out of date.');
      }
      await loadTemplates();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not reconcile templates');
    } finally { setBusy(false); }
  }, [loadTemplates]);

  // Which department group the open template sits under — for the header
  // breadcrumb only, derived from the same grouping the sidebar already shows.
  const selDept = useMemo(() => {
    const g = tmplGroups.find((grp) => (grp.templates || []).some((t) => t.name === sel));
    return g?.department || '';
  }, [tmplGroups, sel]);

  // Revision of the template currently loaded in the editor, sent back on save
  // so a stale row list can't overwrite rows added since (see saveTemplate).
  const [rev, setRev] = useState(null);
  const pickTemplate = (name) => {
    // Don't let an accidental click in the sidebar throw away unsaved edits.
    if (dirty && sel && name !== sel
        && !window.confirm('This template has unsaved changes. Leave without saving?')) return;
    setDirty(false); setRowSel(new Set()); setTmplEmps(null);
    const t = templates.find((x) => x.name === name);
    setSel(name); setTname(name);
    setItems(t ? (t.items || []).map((i) => ({ ...i })) : []);
    setRev(t ? (t.rev ?? 0) : null);
  };
  // The editor must always be showing the sheet the sidebar says is selected.
  //
  // `pickTemplate` sets both together, but it is not the only thing that moves
  // `sel` — a save, a delete, a rename and a reload all do — and any path that
  // changed it without also replacing `items` left the previous sheet's rows
  // on screen under the new sheet's name. Pressing Save there would write one
  // template's KPIs over another's, which is data loss, not a display glitch.
  //
  // Re-syncing whenever the selection or the loaded list changes closes every
  // such path at once. Skipped while `dirty`, so it can never discard edits
  // somebody is in the middle of making.
  useEffect(() => {
    if (dirty) return;
    if (!sel) { setItems([]); setRev(null); return; }
    const t = templates.find((x) => x.name === sel);
    if (!t) return;                       // list still loading — leave as is
    setItems((t.items || []).map((i) => ({ ...i })));
    setRev(t.rev ?? 0);
    // `dirty` is deliberately out of the deps: this resyncs on SELECTION, not
    // on every keystroke that flips the flag.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel, templates]);

  const setItem = (idx, k, v) => { setDirty(true); setItems((arr) => arr.map((it, i) => i === idx ? { ...it, [k]: v } : it)); };
  const addItem = () => { setDirty(true); setItems((arr) => [...arr, { sno: arr.length + 1, kpi: '', kpi_key: '', objective: '', criteria: '', weight: 0 }]); };
  // Picking a KPI copies the master's wording onto the row for display; the
  // kpi_key is what actually binds the row to the master and its data source.
  const pickKpi = (idx, key) => {
    setDirty(true);
    const m = masterFlat.find((x) => x.key === key);
    setItems((arr) => arr.map((it, i) => i === idx
      ? { ...it, kpi_key: key, kpi: m ? m.name : it.kpi,
          objective: m?.objective || '', criteria: m?.criteria || '' }
      : it));
  };
  const delItem = (idx) => { setDirty(true); setItems((arr) => arr.filter((_, i) => i !== idx).map((it, i) => ({ ...it, sno: i + 1 }))); };
  // No `weightTotal` any more. Totalling the STORED weights answered a question
  // nobody should ask: the engine derives each row's share from the KPI count
  // and never reads what is stored, so a stored total of 88 or 0 scored exactly
  // the same as one of 100. What a KPI is worth is `100 / items.length`.

  const saveTemplate = async () => {
    if (!tname.trim()) { toast.error('Template needs a name'); return; }
    setBusy(true);
    try {
      // The stored weight is written as the DERIVED share, not as whatever the
      // row happened to be carrying. Nothing reads it — the engine works the
      // weighting out from the KPI count — but leaving stale values in the
      // database is how 16 of 70 templates came to store a total that is not
      // 100. Saving the true figure lets that drift heal itself over time
      // instead of accumulating.
      const each = items.length ? Number((100 / items.length).toFixed(6)) : 0;
      const r = await kpiApi.saveTemplate({ name: tname.trim(), rev, items: items.map((i, idx) => ({ sno: Number(i.sno) || idx + 1, kpi: i.kpi || '', kpi_key: i.kpi_key || null, objective: i.objective || '', criteria: i.criteria || '', weight: each })) });
      const nSheets = r?.data?.employee_sheets_updated || 0;
      toast.success(`Saved "${tname}" — ${items.length} KPIs, ${each.toFixed(1)}% each`
        + (nSheets ? ` — deleted row(s) also removed from ${nSheets} employee sheet(s)` : ''));
      setRev(r.data?.rev ?? null); setDirty(false);
      await loadTemplates(); setSel(tname.trim());
    } catch (e) {
      // 409 = someone/something added rows since this editor loaded. Reload the
      // fresh rows rather than letting the stale list overwrite them.
      if (e?.response?.status === 409) {
        const t = await kpiApi.templates();
        setTemplates(t.data || []);
        const fresh = (t.data || []).find((x) => x.name === tname.trim());
        if (fresh) { setItems((fresh.items || []).map((i) => ({ ...i }))); setRev(fresh.rev ?? 0); }
        toast.error(`${e.response.data.detail} — the latest rows have been reloaded below.`, { duration: 9000 });
      } else toast.error(e?.response?.data?.detail || 'Save failed');
    }
    finally { setBusy(false); }
  };
  // ---- template productivity actions ----
  const [rowSel, setRowSel] = useState(() => new Set());   // rows ticked for Copy KPIs
  const [tmplEmps, setTmplEmps] = useState(null);          // View Employees panel
  const [dragIdx, setDragIdx] = useState(null);
  // `dirty` is set by every row edit; used to warn before switching template.
  const [dirty, setDirty] = useState(false);
  // ...and before leaving the PAGE. Removing a row with the × is a local edit
  // until Save is pressed, so a reload threw it away silently — the row came
  // back and nothing had said why. Switching template already asked; reloading
  // did not, which is the one way out of this screen nobody confirms.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; return ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const moveRow = (from, to) => {
    if (to < 0 || to >= items.length || from === to) return;
    setItems((arr) => {
      const n = [...arr];
      // Move the WHOLE row — KPI, objective, criteria and weight travel together.
      const [row] = n.splice(from, 1);
      n.splice(to, 0, row);
      return n.map((r, i) => ({ ...r, sno: i + 1 }));
    });
    setDirty(true);
  };

  const duplicateTemplate = async () => {
    if (!sel) { toast.error('Open a template to duplicate'); return; }
    const name = window.prompt(`Duplicate "${sel}" as:`, `${sel} (copy)`);
    if (!name || !name.trim()) return;
    setBusy(true);
    try {
      const r = await kpiApi.duplicateTemplate({ source: sel, name: name.trim() });
      toast.success(`Created "${r.data.name}" with ${r.data.rows} KPI row(s)`);
      await loadTemplates();
      pickTemplate(r.data.name);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Duplicate failed'); }
    finally { setBusy(false); }
  };

  const copySelectedKpis = async () => {
    if (!sel) return;
    if (!rowSel.size) { toast.error('Tick the KPI rows you want to copy'); return; }
    const target = window.prompt(
      `Copy ${rowSel.size} KPI(s) from "${sel}" into which template?\n\n`
      + `Type the exact template name.`, '');
    if (!target || !target.trim()) return;
    setBusy(true);
    try {
      const picked = [...rowSel].map((i) => items[i]).filter(Boolean);
      const r = await kpiApi.copyKpis({
        source: sel, target: target.trim(),
        kpi_keys: picked.map((x) => x.kpi_key).filter(Boolean),
        kpi_names: picked.filter((x) => !x.kpi_key).map((x) => x.kpi),
      });
      const { copied, already_present: dup } = r.data;
      toast.success(`Copied ${copied.length} KPI(s) into "${r.data.target}"`
        + (dup.length ? ` · ${dup.length} already there` : ''));
      setRowSel(new Set());
      await loadTemplates();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Copy failed'); }
    finally { setBusy(false); }
  };

  const viewEmployees = async () => {
    if (!sel) return;
    try {
      const r = await kpiApi.templateEmployees({ name: sel, fy });
      setTmplEmps(r.data);
    } catch (e) { toast.error('Could not load employees'); }
  };

  // `distributeWeight` ("spread evenly") is gone. The weighting is already
  // spread evenly, always, by the engine — the button rewrote a stored number
  // that changed nothing, and its presence implied the weights could be
  // uneven in the first place.

  const deleteTemplate = async () => {
    if (!sel) return;
    if (!window.confirm(`Delete template "${sel}"? Employees already assigned keep their KPIs.`)) return;
    setBusy(true);
    try { await kpiApi.deleteTemplate(sel); toast.success('Deleted'); setSel(''); setItems([]); setTname(''); await loadTemplates(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
    finally { setBusy(false); }
  };
  // The edit form renders inline, directly under the row being edited. It used
  // to sit at the top of the tab, so every rename scrolled you away from the KPI
  // you clicked and then back again.
  const renderEditPanel = () => (
    <div className="border border-[#C7D7FE] bg-[#F5F8FF] rounded-lg p-4">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className="text-sm font-medium text-[#172554]">
          {edit.isNew ? 'New KPI' : 'Edit KPI'}
        </span>
        {/* The key, shown while it is still being decided. It is derived from
            the name and permanent afterwards — every template row, employee
            sheet and recorded mark hangs off it — so it is worth seeing before
            it is fixed rather than discovering later. */}
        <span className="text-[11px] font-mono text-[#5A6377]">
          {edit.isNew ? (keyFromName(edit.name) || '…') : edit.key}
        </span>
        <span className="text-[11px] text-[#98A1B2]">
          {edit.isNew ? 'made from the name, then never changes'
                      : 'key never changes — history stays linked'}
        </span>
        {/* Delete sits past a divider rather than merely beside Save: removing a
            KPI reaches templates, employee sheets and recorded marks, and the
            confirmation that follows reports exactly what would go. */}
        <div className="ml-auto flex gap-2 items-center">
          <Button size="sm" onClick={saveEdit} disabled={busy} data-testid="kpi-master-save">Save</Button>
          <Button size="sm" variant="outline" onClick={() => setEdit(null)}>Cancel</Button>
          {edit.key && (
            <>
              <span className="w-px self-stretch my-0.5 bg-[#DCE6FB]" aria-hidden />
              <Button size="sm" variant="destructive" disabled={busy}
                data-testid="kpi-master-delete" className="tmpl-delete-btn"
                onClick={() => askDeleteKpi(edit.kpi || { key: edit.key, name: edit.name })}>
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </Button>
            </>
          )}
        </div>
      </div>
      <label className="block text-[11px] uppercase text-[#98A1B2] mb-1">KPI name</label>
      <Input value={edit.name} onChange={(e) => setEdit((x) => ({ ...x, name: e.target.value }))}
        className="max-w-lg mb-1 text-sm" data-testid="kpi-master-name" autoFocus />
      {/* A name already taken would, on an upsert, quietly overwrite the KPI
          that owns it. The server refuses that outright; this says so before
          the press rather than after. */}
      {edit.isNew && !!keyFromName(edit.name) && (() => {
        const clash = masterFlat.find((m) => m.key === keyFromName(edit.name))
          || (master?.sources || []).flatMap((g) => g.kpis || [])
               .find((m) => m.key === keyFromName(edit.name));
        return clash ? (
          <p className="text-[11px] text-[#C2151F] mb-3" data-testid="kpi-master-key-clash">
            That name is already used by “{clash.name}”
            {clash.active === false ? ' (retired)' : ''} — choose another, or edit that KPI.
          </p>
        ) : <div className="mb-3" />;
      })()}
      {!edit.isNew && <div className="mb-2" />}
      {/* WHICH SOFTWARE OWNS IT. Only asked when creating: changing the owner
          of a KPI that already exists moves it between groups and is done from
          the list, where the consequences are visible. New KPIs start manual —
          a figure typed in each month — and become automatic the moment a
          source field is chosen below. */}
      {edit.isNew && (
        <>
          <label className="block text-[11px] uppercase text-[#98A1B2] mb-1">
            Where does the figure come from
          </label>
          <div className="flex gap-2 mb-3 flex-wrap" data-testid="kpi-master-source">
            {[['manual', 'Typed in by hand'], ['operations', 'Operations software'],
              ['maintenance', 'Machine Maintenance'], ['npd', 'NPD software'],
              ['hr', 'HR software']].map(([v, label]) => (
              <button key={v}
                onClick={() => {
                  setEdit((x) => ({ ...x, source: v, field: '', denominator_field: '' }));
                }}
                data-testid={`kpi-master-source-${v}`} aria-pressed={edit.source === v}
                className={`text-xs rounded-lg px-3 py-1.5 border ${edit.source === v
                  ? 'bg-[#E8EFFD] border-[#2563EB] text-[#172554] font-medium'
                  : 'bg-white border-[#E6E9F0] text-[#5A6377] hover:bg-[#F5F8FF]'}`}>
                {label}
              </button>
            ))}
          </div>
        </>
      )}
      {/* Which department owns this KPI. The server has accepted this since
          Sep 2026 but nothing ever sent it, so a KPI could only land in
          "Unassigned department" and stay there — there was no way to move it.

          Offered as a list rather than a free text box for the same reason
          sheet names are: a typo creates a department of one, which then reads
          as a real grouping everywhere it appears. A genuinely new department
          is still typeable, deliberately one extra click away. */}
      <label className="block text-[11px] uppercase text-[#98A1B2] mb-1">Department</label>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {newDept ? (
          <Input value={edit.department || ''} autoFocus
            onChange={(e) => setEdit((x) => ({ ...x, department: e.target.value }))}
            placeholder="New department name"
            className="max-w-xs text-sm" data-testid="kpi-master-department-new" />
        ) : (
          <select value={edit.department || ''} data-testid="kpi-master-department"
            onChange={(e) => setEdit((x) => ({ ...x, department: e.target.value }))}
            className="border border-[#E6E9F0] rounded-lg px-2 py-1.5 text-sm text-[#11182B] max-w-xs">
            <option value="">Unassigned department</option>
            {masterDepts.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        )}
        <button onClick={() => setNewDept((v) => !v)}
          data-testid="kpi-master-department-toggle"
          className="text-[11px] text-[#2563EB] hover:underline">
          {newDept ? '\u2190 pick an existing one' : 'add a new department'}
        </button>
      </div>

      {/* Who fills this KPI when it has to be typed in. Set once here and it
          applies to every employee who has this KPI — there is no per-employee
          setting to keep in step. */}
      <label className="block text-[11px] uppercase text-[#98A1B2] mb-1">
        Who fills this KPI by hand
        <span className="normal-case text-[#98A1B2]"> — used only when the software cannot supply it</span>
      </label>
      <div className="flex gap-2 mb-1 flex-wrap" data-testid="kpi-master-responsible">
        {[['hr', 'HR', 'HR fills it (default)'],
          ['hod', 'Reporting Head', "The employee's reporting head fills it"]].map(([v, label, hint]) => (
          <button key={v} onClick={() => setEdit((x) => ({ ...x, responsible: v }))}
            title={hint} data-testid={`kpi-master-responsible-${v}`}
            className={`text-xs rounded-lg px-3 py-1.5 border ${edit.responsible === v
              ? 'bg-[#E8EFFD] border-[#2563EB] text-[#172554] font-medium'
              : 'bg-white border-[#E6E9F0] text-[#5A6377] hover:bg-[#F5F8FF]'}`}>
            {label}
          </button>
        ))}
      </div>
      <p className="text-[11px] text-[#98A1B2] mb-3">
        {edit.responsible === 'hod'
          ? "Goes to the employee's reporting head. If that head has no email, it goes further up the org chart. "
            + 'Employees are never asked to fill their own KPI.'
          : 'Stays with HR — nobody outside HR is asked for it.'}
      </p>
      {/* Which way is good. The detail pane has always SHOWN this; there was no
          way to change it, so a KPI that read "Higher is better" on a rejection
          figure stayed that way. It is the KPI's own statement of intent and it
          is what every reader goes by.

          It does not itself move a mark: the engine scores from the ladder's
          own operators (>= rungs climb, <= rungs fall). So the note underneath
          says plainly when the two disagree, rather than silently rewriting
          rungs somebody agreed to. */}
      <label className="block text-[11px] uppercase text-[#98A1B2] mb-1">
        Direction
        <span className="normal-case text-[#98A1B2]"> — which way is a better result</span>
      </label>
      <div className="flex gap-2 mb-1 flex-wrap" data-testid="kpi-master-direction">
        {[['higher', 'Higher is better', 'More of this is a better month — efficiency, compliance, output'],
          ['lower', 'Lower is better', 'Less of this is a better month — rejections, breakdowns, delays']]
          .map(([v, label, hint]) => (
          <button key={v} onClick={() => chooseDirection(v)} disabled={ladderBusy}
            title={hint} data-testid={`kpi-master-direction-${v}`}
            aria-pressed={edit.direction === v}
            className={`text-xs rounded-lg px-3 py-1.5 border ${edit.direction === v
              ? 'bg-[#E8EFFD] border-[#2563EB] text-[#172554] font-medium'
              : 'bg-white border-[#E6E9F0] text-[#5A6377] hover:bg-[#F5F8FF]'}`}>
            {label}
          </button>
        ))}
      </div>
      {(() => {
        const rungs = (edit.bands || []).filter((b) => !('else' in b));
        if (!rungs.length) {
          return <p className="text-[11px] text-[#98A1B2] mb-3" data-testid="kpi-direction-note">
            {ladderBusy ? 'Building a marking ladder from the latest figure…'
              : ladderWanted ? 'Waiting for the latest figure, then the ladder is built from it…'
              : typeof reading?.value === 'number'
                ? 'No ladder yet — choosing a direction builds one from the figure below.'
                : edit.field
                  ? 'No ladder yet — choose a direction and one is built from the figure '
                    + 'as soon as it is read.'
                  : 'No ladder yet. Point this KPI at a number and choosing a direction '
                    + 'will build one from it.'}
          </p>;
        }
        const ladder = rungs.some((b) => 'gte' in b) ? 'higher' : 'lower';
        return ladder === edit.direction ? (
          <p className="text-[11px] text-[#98A1B2] mb-3">
            Matches the ladder below, which {ladder === 'higher'
              ? 'awards more marks as the figure rises.' : 'awards more marks as the figure falls.'}
          </p>
        ) : (
          <p className="text-[11px] text-[#B14A0E] mb-3" data-testid="kpi-direction-mismatch">
            Note: the ladder below is written the other way round — it awards more marks as the
            figure {ladder === 'higher' ? 'rises' : 'falls'}. That is sometimes deliberate (a
            target-derived ladder is written worst-first), so nothing is changed automatically.
            Check the rungs if this is not what you meant.
          </p>
        );
      })()}

      {edit.source === 'manual' ? (
        <p className="text-xs text-[#5A6377]">
          This KPI is manual — its value is typed in each month, so there is no software field to map.
        </p>
      ) : (
        <>
          {/* One KPI reads exactly ONE number, chosen from the list the
              imported months PROVED exists. This replaced a permanently-open
              200-row table that every KPI edit had to be scrolled past; the row
              somebody wants is nearly always three typed letters away.

              Bound once and left alone: the catalogue says what a number IS,
              and importing next month changes only its value. */}
          <label className="block text-[11px] uppercase text-[#98A1B2] mb-1">
            Source field
            <span className="normal-case text-[#98A1B2]"> — one KPI reads one number</span>
          </label>
          {/* CHOSEN, never typed. The raw-path box that used to sit beside this
              is gone: a hand-typed path is unchecked, and one the file does not
              carry reads nothing all month while looking exactly like a working
              binding. Everything offered here was proved to resolve by an
              actual import. */}
          <MetricPicker source={edit.source === 'operations' ? 'ops'
                            : edit.source === 'maintenance' ? 'maint' : edit.source}
              value={edit.field || ''}
              denominator={edit.denominator_field || ''}
              onChange={(path, m) => setEdit((x) => ({
                ...x,
                // A BUILT figure is two paths divided. The KPI stores those two
                // rather than the figure's own name, which is what lets the
                // scorer read it with no special case — and is also why the
                // denominator has to be written here. Picking an ordinary
                // number CLEARS it: leaving the old divisor behind would turn
                // the next field into a ratio nobody asked for.
                field: m?.derived?.field || path,
                denominator_field: m?.derived?.denominator_field || '',
                // A ratio reads as a percentage, always. Otherwise the unit is
                // only FILLED IN, never overwritten — a uom somebody set by
                // hand is a decision, and picking a field is not the moment to
                // undo it.
                kpi: { ...(x.kpi || {}),
                       uom: m?.derived?.denominator_field ? '%'
                         : (x.kpi?.uom || m?.uom || '') },
            }))} />
          {/* If the number genuinely is not there, the answer is to import the
              month that carries it — not to type a path and hope.

              Except for HR, which is not an imported source at all: its figures
              are computed here from this app's own registers. Telling somebody
              to import an HR month sends them looking for a file that does not
              exist and never will. */}
          <p className="text-[11px] text-[#98A1B2] mt-1.5 mb-1">
            {edit.source === 'hr'
              ? 'These are worked out by this app from its own registers — attrition, '
                + 'late coming, suggestions, training. There is no file to import, and '
                + 'the figure below is last completed month.'
              : "Every number here came from an imported month. If what you need is "
                + "missing, import that software's month on the Source Data tab."}
          </p>
          {/* What the chosen field ACTUALLY reads, per plant, with the mark the
              ladder below would give it. Same numbers Source Data shows, and
              the marks come from the real resolver on the server \u2014 there is no
              second copy of the arithmetic here, which is how a preview comes
              to disagree with the mark it is previewing. */}
          <FieldPreview source={edit.source} field={edit.field}
            denominator={edit.denominator_field} uom={edit.kpi?.uom || ''}
            bands={edit.bands} activeUnit={activeUnit} onReading={setReading} />
        </>
      )}

      {/* The marking ladder. Applies whether the value is pulled or typed in —
          a KPI without one cannot be scored by anything, which is why 79% of
          the catalogue was "manual" and stayed that way. */}
      <div className="mt-4 pt-4 border-t border-[#DCE6FB]">
        {/* The ladder is built silently and the rungs below are the whole
            story: every one is editable, so a derived threshold is corrected
            in place rather than through a separate "provisional" panel. */}
        {/* The Accident register is marked from FOUR counts, not one value, so
            a single threshold table has nothing useful to say about it. What
            used to sit here was an identity ladder that passed the finished
            mark through unchanged — editable, and with no effect — while the
            severity weights that decided the mark were hardcoded out of reach.
            The four real ladders are shown instead. */}
        {edit.field === 'hr.safety_shop_floor' ? (
          <AccidentLadders activeUnit={activeUnit} plants={plants} />
        ) : (
        <BandLadder bands={edit.bands} uom={edit.kpi?.uom || ''}
          plants={plants} scope={edit.scope || []} mode={edit.scopeMode || 'all'}
          onScope={changeScope} onMode={changeScopeMode}
          overrides={Object.keys(edit.bandsByUnit || {})}
          inherited={!!edit.bandsInherited} onRemoveOverride={removeOverride}
          onChange={(b) => setEdit((x) => ({ ...x, bands: b, bandsInherited: false }))} />
        )}
        {/* The offer stays available after the first direction click — a KPI
            that already has rungs gets the builder as a button, never as an
            automatic overwrite of thresholds somebody agreed. */}
        {!edit.built && (edit.bands || []).length > 0 && typeof reading?.value === 'number' && (
          <button onClick={() => {
            if (window.confirm(
              `Replace the ${(edit.bands || []).length} step(s) on "${edit.name}" with a ladder `
              + `built around the latest reading of ${reading.value}${edit.kpi?.uom ? ' ' + edit.kpi.uom : ''}?\n\n`
              // "Undo" lived in the provisional panel that is gone; Cancel is
              // what backs this out now, so that is what the prompt offers.
              + 'Every step stays editable, and Cancel discards it. '
              + 'Nothing is saved until you press Save.')) buildLadder(edit.direction);
          }} disabled={ladderBusy}
            className="mt-2 text-[11px] text-[#2563EB] hover:underline"
            data-testid="kpi-ladder-build">
            build a ladder from the latest figure instead
          </button>
        )}
      </div>
    </div>
  );

  // Templates are global: KPI, objective, criteria and weight only. Monthly
  // actuals/marks are per employee+unit+month and live on the employee KPI tab,
  // so no Operations fetch belongs on this screen.
  const period = prevPeriod();

  // ---- global KPI master (catalogue grouped by owning software) ----
  const [master, setMaster] = useState(null);
  // Filters are derived from the master records themselves, so a new KPI or a
  // new source appears here without any change to this file.
  const [mq, setMq] = useState('');
  const [mDept, setMDept] = useState('');
  // Filter to KPIs that read a number but have no ladder to convert it to marks.
  const [mNoLadder, setMNoLadder] = useState(false);
  // Show the withdrawn ones. OFF by default: they score nothing and cannot be
  // put on a sheet, so they are noise in every ordinary use of this screen.
  // Reachable, though — a retired KPI still owns its key, which is what stops
  // a new KPI taking that name, so you have to be able to find and re-activate
  // one.
  const [mRetired, setMRetired] = useState(false);
  // "N awaiting field" is a to-do, so it is also a filter — clicking the badge
  // shows exactly the KPIs it counted, rather than leaving you to find them.
  const [mAwaiting, setMAwaiting] = useState(false);
  const masterDepts = React.useMemo(() => {
    const s = new Set();
    (master?.sources || []).forEach((g) => (g.kpis || []).forEach((k) => k.department && s.add(k.department)));
    return [...s].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [master]);
  // Flat list of every active master KPI — the only source the template picker
  // offers, so no KPI name is ever typed (or hardcoded) again.
  const masterFlat = React.useMemo(() => {
    const out = [];
    (master?.sources || []).forEach((g) => (g.kpis || []).forEach((k) => {
      if (k.active !== false) out.push({ ...k, sourceLabel: g.label, sourceKey: g.source });
    }));
    return out.sort((a, b) => a.name.localeCompare(b.name));
  }, [master]);
  // The same list WITH the retired ones. The picker shows those too, greyed and
  // unpickable, because dropping them silently is what makes the dialog read as
  // broken: the master holds 225 KPIs, the picker offered 202, and the 23
  // missing ones were missing for a reason nothing on screen ever gave. A KPI
  // somebody can see in KPI Master has to be findable here — and then told why
  // it cannot be used, which is the same thing the server says if you try.
  const masterAll = React.useMemo(() => {
    const out = [];
    (master?.sources || []).forEach((g) => (g.kpis || []).forEach(
      (k) => out.push({ ...k, sourceLabel: g.label, sourceKey: g.source })));
    return out.sort((a, b) => a.name.localeCompare(b.name));
  }, [master]);

  // ---- searchable KPI picker for template rows ----
  // A native <select> of 222 options is unusable: there is no way to search it
  // and HR had to scroll the whole master to find one KPI. This keeps the same
  // "pick from the master, never type a name" rule — it only makes finding the
  // KPI possible. Nothing about the master, the template or the mapping changes.
  const [pickerRow, setPickerRow] = useState(null);   // row index being picked for
  const [pq, setPq] = useState('');
  const [pDept, setPDept] = useState('');
  const [pRole, setPRole] = useState('');
  const [pSrc, setPSrc] = useState('');
  const [pMode, setPMode] = useState('');             // '' | 'auto' | 'manual'

  // Same rule the backend uses (_kpi_row_mode): a KPI is automatic when a
  // software owns it AND a source field is mapped. Owned but unmapped is still
  // manual — nothing can read it yet.
  const kpiMode = (m) => (m.sourceKey && m.sourceKey !== 'manual' && m.field) ? 'auto' : 'manual';

  const pickerDepts = useMemo(() =>
    [...new Set(masterFlat.map((m) => m.department).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })), [masterFlat]);  const pickerRoles = useMemo(() =>
    [...new Set(masterFlat.flatMap((m) => m.roles || []))]
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })), [masterFlat]);
  const pickerSources = useMemo(() =>
    [...new Map(masterFlat.map((m) => [m.sourceKey, m.sourceLabel])).entries()], [masterFlat]);

  const pickerResults = useMemo(() => {
    const q = pq.trim().toLowerCase();
    return masterAll.filter((m) =>
      (!pDept || m.department === pDept)
      && (!pRole || (m.roles || []).includes(pRole))
      && (!pSrc || m.sourceKey === pSrc)
      && (!pMode || kpiMode(m) === pMode)
      // Name and key, as specified — plus department/source so a single box
      // covers the obvious searches without a "search by" selector.
      && (!q || [m.name, m.key, m.department, m.sourceLabel]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q))));
  }, [masterAll, pq, pDept, pRole, pSrc, pMode]);

  // KPIs this sheet ALREADY carries. The same KPI twice is never meant: the
  // engine gives every row an equal share of 100, so a KPI listed twice is
  // silently worth double what the sheet says, and the person is marked on it
  // twice — invisible to anyone reading the sheet.
  //
  // The row being picked FOR is excluded, or re-opening the picker on a filled
  // row would show that row's own KPI as unavailable to itself.
  //
  // Deliberately scoped to THIS sheet. The same KPI belongs on many different
  // sheets — press strokes score Production and the Plant Head alike — and
  // nothing here restricts that.
  const takenKeys = useMemo(() => new Set(
    items.filter((_, i) => i !== pickerRow)
      .map((it) => it.kpi_key).filter(Boolean)), [items, pickerRow]);

  const openPicker = (idx) => {
    setPickerRow(idx);
    setPq(''); setPDept(''); setPRole(''); setPSrc(''); setPMode('');
  };
  const choosePickerKpi = (key) => {
    // Belt and braces: the row is not clickable, but a stray Enter on a
    // focused row must not add a duplicate — or a retired KPI, which the save
    // endpoint refuses outright.
    if (takenKeys.has(key)) return;
    if (masterAll.find((m) => m.key === key)?.active === false) return;
    pickKpi(pickerRow, key);
    setPickerRow(null);
  };

  // Grouping for the master table. By department reads as "what does Quality get
  // measured on"; by source reads as "what does Operations own". Both are useful,
  // so it's a toggle rather than a decision made for the reader.
  const [mGroupBy, setMGroupBy] = useState('department');
  // Which groups are open. Collapsed to start: 230 KPIs is a wall of rows, and
  // the useful first view is the list of departments with their counts. Held by
  // group id, and cleared when the grouping changes because 'ACCOUNTS' and
  // 'Operations' are ids from two different sets.
  const [openGroups, setOpenGroups] = useState(() => new Set());
  useEffect(() => { setOpenGroups(new Set()); }, [mGroupBy]);
  const toggleGroup = (id) => setOpenGroups((s) => {
    const n = new Set(s);
    if (!n.delete(id)) n.add(id);
    return n;
  });
  // ---- KPI Master: usage, preview ----
  // `mRole` and `mScope` are gone with the controls that set them. Search
  // already matches on role, and "frequently used" was a filter nobody could
  // act on — a KPI being on three templates is not a reason to look at it.
  const [usage, setUsage] = useState(null);           // {by_key: {key: [templates]}}
  const [rolesOpen, setRolesOpen] = useState(null);   // key whose roles are expanded
  const [preview, setPreview] = useState(null);       // KPI shown in the side panel
  // Which KPIs are scored against a per-plant target rather than one shared
  // ladder. Taken from the server's own answer, so a target-driven KPI added
  // there shows its targets here with no change in this file.
  const [targetKpis, setTargetKpis] = useState(() => new Set());
  useEffect(() => {
    kpiApi.unitTargets().then((r) => {
      const s = new Set();
      (r.data?.targets || []).forEach((t) => (t.scores_kpis || []).forEach((k) => s.add(k)));
      setTargetKpis(s);
    }).catch(() => { /* targets are optional — Advanced simply omits them */ });
  }, []);
  useEffect(() => {
    if (!['master', 'templates'].includes(tab) || usage) return;
    kpiApi.masterUsage().then((r) => setUsage(r.data)).catch(() => { /* non-fatal */ });
  }, [tab, usage]);

  const usedIn = useCallback((k) => (usage?.by_key || {})[k] || [], [usage]);

  // `addKpiToTemplate` used to live here. It asked for the template name via a
  // browser prompt — you had to type one of 82 names exactly right, and a typo
  // just failed. Both ways of assigning a KPI now have a proper picker:
  //
  //   a whole role  -> Templates tab, "+ Add row", choose the KPI from a list
  //   one person    -> that employee's KPI tab, "+ Add a KPI"
  //
  // The `copyKpis` API it called is untouched and still used by the Templates
  // tab's own copy/duplicate actions.
  const masterGroups = useMemo(() => {
    const src = master?.sources || [];
    if (mGroupBy === 'source') {
      return src.map((g) => ({
        id: g.source, label: g.label, owns: g.owns, months: g.months_uploaded,
        pending: g.pending, rows: (g.kpis || []).map((k) => ({ ...k, _g: g })),
      }));
    }
    const by = new Map();
    src.forEach((g) => (g.kpis || []).forEach((k) => {
      // KPIs with no department are real and must stay visible, so they get
      // their own bucket at the end rather than being dropped.
      const d = (k.department || '').trim() || 'Unassigned department';
      if (!by.has(d)) by.set(d, []);
      by.get(d).push({ ...k, _g: g });
    }));
    // Alphabetical, case-insensitively — department names are stored as the team
    // writes them ('ACCOUNTS', 'Maintenance'), and a case-sensitive sort would
    // put every shouted name before every title-cased one, which is what made
    // this list look arbitrary. Same order as the Templates sidebar.
    return [...by.entries()]
      .sort((a, b) => (a[0] === 'Unassigned department' ? 1
                     : b[0] === 'Unassigned department' ? -1
                     : a[0].localeCompare(b[0], undefined, { sensitivity: 'base' })))
      .map(([d, rows]) => ({
        id: d, label: d,
        // Which software feeds this department, so ownership is still visible
        // once the grouping no longer shows it.
        owns: [...new Set(rows.map((r) => r._g.label))].join(' · '),
        // Same rule as the server's per-source count: a LIVE KPI the software
        // owns, with no number mapped yet. Retired ones are excluded — they are
        // hidden from the list below, so counting them advertised work that
        // could never be found, let alone done.
        pending: rows.filter((r) => !r.field && r.active !== false
                                    && r._g.kind === 'api').length,
        rows: rows.sort((a, b) => a.name.localeCompare(b.name)),
      }));
  }, [master, mGroupBy]);
  // --- KPI master editing: rename, and map a KPI to a real source field ---
  // The field list used to be fetched HERE, per month, and held in three bits
  // of state beside the catalogue that named it. MetricPicker now owns all of
  // that: it reads the catalogue, which already carries the path, its English
  // name and its per-plant figures. One source instead of two that could
  // disagree — and nothing month-shaped to get wrong, which is what produced
  // the "live from ," with no month in it.
  const [edit, setEdit] = useState(null);      // {key, name, field, source}
  // Reveals the raw path input for fields the software does not expose yet.
  const [newDept, setNewDept] = useState(false);
  // The latest figure the open KPI actually reads, handed up by FieldPreview.
  // The ladder builder below is anchored on it — it is the only number in the
  // panel that nobody made up.
  const [reading, setReading] = useState(null);
  // ---- plant-wise ladders -------------------------------------------------
  // The plant master, read once. Names come from the server rather than being
  // listed here: a plant added in Settings has to appear without a code change,
  // and a guessed identifier would silently write an override nothing reads.
  const [plants, setPlants] = useState([]);
  useEffect(() => {
    masterApi.units()
      .then((r) => {
        const rows = Array.isArray(r.data) ? r.data : (r.data?.units || []);
        const live = rows.filter(
          (u) => u.active !== false && u.name && u.name !== 'Corporate Office');
        // "Unit 3 (IMT)" does not fit in a chip; the parenthesis is the site,
        // and the part before it is what everybody calls the plant.
        const shorten = (n) => String(n).replace(/\s*\(.*\)\s*$/, '').trim() || n;
        // ...unless two plants shorten to the SAME chip, which the plant master
        // currently does: "Sanber Labs (SGM Nagar)" and "Sanber Labs" are two
        // separate records holding different people. Two identical chips are
        // worse than a long one — an override written to the wrong twin is
        // invisible, because it looks exactly like the right one.
        const clashes = new Set();
        const seen = new Set();
        live.forEach((u) => {
          const s = shorten(u.name);
          if (seen.has(s)) clashes.add(s); else seen.add(s);
        });
        setPlants(live
          .map((u) => ({
            name: u.name,
            short: clashes.has(shorten(u.name)) ? u.name : shorten(u.name),
            order: /unit\s*1/i.test(u.name) ? 1 : /unit\s*3/i.test(u.name) ? 2 : 3,
          }))
          .sort((a, b) => a.order - b.order || a.name.localeCompare(b.name)));
      })
      .catch(() => setPlants([]));
  }, []);

  // Decode the server's Mongo-safe keys back to plant names. `_mongo_key`
  // swaps '.' and '$', which no current plant name contains — decoded anyway
  // so a plant named "A.M. Shop" tomorrow does not read as a different one.
  const unMongo = (s) => String(s).replace(/．/g, '.').replace(/＄/g, '$');
  // DEEP COPIES, deliberately. Two plants given the same ladder hold two
  // separate copies on the server; handing the editor one shared array would
  // re-link them in the browser, and editing Unit 1's rungs would move Unit 3's
  // on screen — the exact behaviour this feature exists to prevent.
  const bandsByUnitOf = (k) => Object.fromEntries(
    Object.entries(k?.bands_by_unit || {})
      .map(([u, b]) => [unMongo(u), JSON.parse(JSON.stringify(b || []))]));
  const copyBands = (b) => JSON.parse(JSON.stringify(b || []));

  const openEdit = (k, g) => {
    // 'employee' was withdrawn as a choice; a record still carrying it reads as
    // the reporting head, which is what the server stores on the next save.
    setEdit({ key: k.key, name: k.name, field: k.field || '', source: g.source,
              department: k.department || '',
              responsible: k.responsible === 'employee' ? 'hod' : (k.responsible || 'hr'),
              // Carried so the live preview divides the way the scorer does.
              // Without it a ratio KPI previewed its RAW NUMERATOR — Inspection
              // Efficiency showed "2,608,577 %" beside a Source Data row
              // reading 83.7%, because 2,608,577 ÷ 3,116,773 × 100 never
              // happened. A preview that does different arithmetic from the
              // engine is worse than no preview.
              denominator_field: k.denominator_field || '',
              direction: k.direction === 'lower' ? 'lower' : 'higher',
              // Opens on the SHARED ladder, always. That is what the editor has
              // always shown, so a KPI with no overrides behaves exactly as it
              // did; a KPI with overrides still opens on the rule that governs
              // every plant that has none.
              bands: copyBands(k.bands),
              scopeMode: 'all',
              scope: [],
              sharedBands: copyBands(k.bands),
              bandsByUnit: bandsByUnitOf(k),
              kpi: k });
    setNewDept(false);
    setReading(null);
  };

  // The key a name would become. Mirrors the server exactly
  // (`key.strip().lower().replace(" ", "_")`), so what the screen shows is what
  // gets stored — a key is permanent and history hangs off it, so it must not
  // be a surprise.
  const keyFromName = (n) => String(n || '').trim().toLowerCase().replace(/ /g, '_');

  const newKpi = () => {
    setEdit({
      isNew: true, key: '', name: '',
      // Manual by default. A KPI is only automatic once somebody points it at
      // a number a source actually publishes, and guessing an owner here would
      // put a KPI in Operations' list that Operations has never heard of.
      source: 'manual',
      department: mDept || '', responsible: 'hr', direction: 'higher',
      field: '', denominator_field: '',
      bands: [], scopeMode: 'all', scope: [], sharedBands: [], bandsByUnit: {},
      kpi: {},
    });
    setNewDept(false);
    setReading(null);
  };

  // ---- switching which plants the ladder on screen belongs to -------------
  // Each scope's ladder is held separately, so moving between them cannot
  // carry one plant's rungs into another's. Unsaved work is confirmed before
  // it is dropped — switching scope is the one action here that discards an
  // edit without looking like it.
  const scopeBands = (e, scope) => {
    if (!scope.length) return { bands: copyBands(e.sharedBands), inherited: false };
    const own = scope.map((p) => e.bandsByUnit?.[p]).filter((b) => b && b.length);
    if (!own.length) return { bands: copyBands(e.sharedBands), inherited: true };
    // Two plants picked whose ladders already differ: show the first and say
    // so, rather than silently presenting one as if it governed both.
    const differ = own.length > 1
      && own.some((b) => JSON.stringify(b) !== JSON.stringify(own[0]));
    return { bands: copyBands(own[0]), inherited: false, differ };
  };

  // What is on screen right now, for the scope it was loaded for. Used to tell
  // an untouched ladder from an edited one before discarding it.
  const loadedBands = (x) => (!x.scope?.length
    ? (x.sharedBands || [])
    : (x.bandsByUnit?.[x.scope[0]] || x.sharedBands || []));

  const changeScope = (next, nextMode) => {
    setEdit((x) => {
      if (!x) return x;
      const dirty = JSON.stringify(x.bands || []) !== JSON.stringify(loadedBands(x) || []);
      if (dirty && !window.confirm(
        'The ladder on screen has unsaved changes.\n\n'
        + 'Switching will discard them. Continue?')) {
        return x;
      }
      const { bands, inherited, differ } = scopeBands(x, next);
      if (differ) {
        toast('Those plants have different ladders — showing the first. '
              + 'Saving applies what you see to both.', { icon: '⚠️' });
      }
      // `built` describes a ladder derived for the scope that was open; it no
      // longer describes the one being shown.
      return { ...x, scope: next, scopeMode: nextMode ?? x.scopeMode,
               bands, bandsInherited: !!inherited, built: null };
    });
  };

  // Changing MODE is a scope change too — All Plants clears the selection, and
  // moving from "selected plants" to "single plant" keeps at most one.
  const changeScopeMode = (m) => {
    const cur = edit?.scope || [];
    changeScope(m === 'all' ? [] : m === 'one' ? cur.slice(0, 1) : cur, m);
  };

  // Put one plant back on the shared ladder. Explicit, confirmed, and separate
  // from Save — an empty editor is never read as a request to delete.
  const removeOverride = async () => {
    const held = (edit?.scope || []).filter((p) => (edit.bandsByUnit?.[p] || []).length);
    if (!held.length) return;
    if (!window.confirm(
      `Remove the custom ladder from ${held.join(' and ')}?\n\n`
      + `${held.length > 1 ? 'They' : 'It'} will go back to being marked against the `
      + 'shared ladder.\n\nThe shared ladder and every other plant are untouched.')) return;
    setBusy(true);
    try {
      const r = await kpiApi.saveBandsForScope(edit.key, { scope: held, mode: 'remove' });
      const stored = r.data?.bands_by_unit || {};
      const leftover = held.filter((p) => (stored[p] || []).length);
      if (leftover.length) {
        toast.error(`Consistency error: ${leftover.join(', ')} still has a custom ladder `
                    + 'after the removal. Nothing else was changed — please re-check.');
        return;
      }
      toast.success(`${held.join(' and ')} back on the shared ladder`);
      await reseedAfterSave(stored, r.data?.shared_bands);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not remove the custom ladder');
    } finally { setBusy(false); }
  };

  // Re-seed the open editor from what the SERVER says is stored, keeping the
  // mode and the selected plants on screen. Before this, saving called
  // `setEdit(null)` — the editor closed, and reopening it started a fresh
  // session back on All Plants. That is the whole of the "my selection reset"
  // bug: the overrides were saved correctly every time.
  const reseedAfterSave = async (storedByUnit, storedShared, key = null) => {
    const r = await kpiApi.master();
    setMaster(r.data);
    const want = key || edit.key;
    const fresh = (r.data?.sources || [])
      .flatMap((g) => (g.kpis || []).map((k) => ({ ...k, _g: g })))
      .find((k) => k.key === want);
    // `preview` is the object the list captured when the row was clicked. Left
    // stale it re-seeds the editor from pre-save data, which is why the panel
    // reported "no plant has its own ladder" straight after saving two.
    if (fresh) setPreview(fresh);
    setEdit((x) => {
      if (!x) return x;
      const next = {
        ...x,
        kpi: fresh || x.kpi,
        sharedBands: copyBands(storedShared ?? fresh?.bands ?? x.sharedBands),
        bandsByUnit: storedByUnit
          ? Object.fromEntries(Object.entries(storedByUnit)
              .map(([u, b]) => [unMongo(u), copyBands(b)]))
          : bandsByUnitOf(fresh || {}),
        built: null,
      };
      const { bands, inherited } = scopeBands(next, x.scope || []);
      return { ...next, bands, bandsInherited: !!inherited };
    });
    const t = await kpiApi.templates(); setTemplates(t.data || []);
  };

  // ---- build a marking ladder from the figure the KPI already reads ----
  // 51 automatic KPIs pull a number every month and score nothing, because no
  // ladder exists to turn that number into marks. Writing five thresholds by
  // hand for each is the reason they stayed empty.
  //
  // So choosing a direction builds one. The anchor is the live reading, the
  // step is derived from it server-side (one function, the same one the scorer
  // uses), and the result is stamped PROVISIONAL: a ladder built from what the
  // plant happened to do in September is a starting point, not a target the
  // business agreed. Every rung stays editable, and nothing is written until
  // Save is pressed.
  const [ladderBusy, setLadderBusy] = useState(false);
  // A direction chosen BEFORE the figure has arrived. The panel reads the
  // latest month over the network, so "Lower is better" is routinely pressed
  // while that is still in flight — and refusing with "no figure to build
  // from" is indistinguishable, from the chair, from the feature not working.
  // So the intent is held and spent the moment the reading lands.
  const [ladderWanted, setLadderWanted] = useState('');
  const buildLadder = async (direction, step) => {
    const v = reading?.value;
    if (typeof v !== 'number') {
      toast.error('No figure to build from yet — map a source field first, or add the steps by hand.');
      return;
    }
    setLadderBusy(true);
    try {
      const r = await kpiApi.suggestLadder({
        value: v, direction, uom: edit?.kpi?.uom || '',
        step: step ?? null,
        // Today's reading is worth 2 of 4. Anchoring it at 4 would declare the
        // current month excellent by construction and quietly hand every plant
        // full marks for standing still.
        anchor_score: 2,
      });
      setEdit((x) => x && ({
        ...x, bands: r.data.bands,
        built: { ...r.data, direction, value: v, period: reading?.period || '',
                 unit: reading?.unit || '' },
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not build a ladder from that figure');
    } finally { setLadderBusy(false); }
  };

  // Direction is the KPI's statement of intent, and on a KPI with no ladder it
  // is also enough to derive one. An EXISTING ladder is never overwritten here
  // — somebody agreed those rungs — so the panel offers a rebuild instead.
  const chooseDirection = (v) => {
    setEdit((x) => x && ({ ...x, direction: v }));
    const rungs = (edit?.bands || []).filter((b) => !('else' in b));
    if (!(!rungs.length || edit?.built)) return;
    // The figure may not be back yet. Record what was asked for either way —
    // the effect below spends it the moment the reading lands, so pressing a
    // direction always ends in a ladder rather than in an error toast that
    // depended on how fast the network was.
    if (typeof reading?.value === 'number') buildLadder(v);
    else setLadderWanted(v);
  };

  // The figure arrived after a direction was chosen. Build the ladder now.
  // Keyed on the VALUE, not the reading object, which is replaced on every
  // read — on identity this would re-fire and overwrite rungs being edited.
  const readingValue = typeof reading?.value === 'number' ? reading.value : null;
  useEffect(() => {
    if (!ladderWanted || readingValue === null || ladderBusy) return;
    setLadderWanted('');
    buildLadder(ladderWanted);
    // buildLadder is recreated each render and closes over exactly the state
    // this effect waits for; re-running on its identity would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ladderWanted, readingValue]);

  // A different KPI opened, or the field re-pointed, retires the intent — the
  // ladder would otherwise be built from whatever number happened to arrive
  // next, which is a different KPI's figure.
  useEffect(() => { setLadderWanted(''); }, [edit?.key, edit?.field]);
  // Picking a different KPI while the editor is open must move the editor with
  // it. The right pane renders the editor whenever `edit` is set, so without
  // this the list highlight moved and the form kept showing the KPI opened
  // before it — a form labelled with one KPI's key while another is selected,
  // which is how the wrong record gets edited.
  //
  // Keyed on the KPI key rather than the object: `preview` is replaced on every
  // list re-render, and re-running on identity would reset the form mid-typing.
  const editKey = edit?.key;
  useEffect(() => {
    if (!editKey || !preview?.key || preview.key === editKey) return;
    openEdit(preview, preview._g);
    // openEdit is recreated each render and is safe to omit; re-running it on
    // identity alone would clobber the form while it is being filled in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview?.key, editKey]);

  const saveEdit = async () => {
    if (!edit?.name?.trim()) { toast.error('KPI name cannot be empty'); return; }
    // A new KPI's key comes from its name and is permanent from the moment it
    // is written, so the clash is caught here as well as on the server — the
    // server refuses, but being told before the press is the better experience.
    const newKey = edit.isNew ? keyFromName(edit.name) : edit.key;
    if (edit.isNew) {
      if (!newKey) {
        toast.error('The name needs at least one letter or number to make a key from.');
        return;
      }
      const taken = (master?.sources || []).flatMap((g) => g.kpis || [])
        .find((m) => m.key === newKey);
      if (taken) {
        toast.error(`"${taken.name}" already uses the key ${newKey}. Choose another name.`);
        return;
      }
    }
    // Emptying a ladder that EXISTS is destructive and silent: the KPI keeps
    // reading its number every month and simply stops awarding marks, which on
    // screen is indistinguishable from a KPI that never had a rule. It has
    // already happened once — Inspection Efficiency lost its ladder on a save
    // that was only meant to change the source field. Clearing is still
    // allowed; it just has to be meant.
    const plantScope = (edit.scope || []).length > 0;
    if (!plantScope && (edit.kpi?.bands || []).length && !(edit.bands && edit.bands.length)
        && !window.confirm(
          `"${edit.name}" has a marking ladder and you are about to remove it.\n\n`
          + 'The KPI will keep reading its number but will stop awarding marks.\n\n'
          + 'Clear the ladder?')) {
      return;
    }
    // An empty editor on a plant scope is a mistake, not a deletion. Removing
    // an override has its own button, its own confirmation and its own API
    // mode — see `removeOverride`.
    if (plantScope && !(edit.bands && edit.bands.length)) {
      toast.error('No steps to save. Add at least one step — or use '
                  + '"Remove the custom ladder" to put the plant back on the shared one.');
      return;
    }
    if (edit.scopeMode === 'one' && edit.scope.length !== 1) {
      toast.error('Pick the one plant whose ladder you are editing.');
      return;
    }
    if (edit.scopeMode === 'many' && edit.scope.length < 2) {
      toast.error('Tick at least two plants — or switch to Single Plant for one.');
      return;
    }
    // Overwriting SEVERAL plants that already disagree replaces more than one
    // agreed rule in a single press, and the editor can only ever show one of
    // them. Name them before it happens.
    if (plantScope) {
      const differing = edit.scope.filter((p) => {
        const own = edit.bandsByUnit?.[p];
        return own && own.length
          && JSON.stringify(own) !== JSON.stringify(edit.bands || []);
      });
      if (differing.length > 1 && !window.confirm(
        `${differing.join(' and ')} already have DIFFERENT custom ladders.\n\n`
        + 'Saving replaces both with the one on screen.\n\nContinue?')) {
        return;
      }
    }
    setBusy(true);
    try {
      const k = edit.kpi;
      const builtUnchanged = !!edit.built
        && JSON.stringify(edit.bands || []) === JSON.stringify(edit.built.bands || []);
      await kpiApi.saveMaster({
        key: newKey, name: edit.name.trim(), source: edit.source,
        // Says "this is a creation", so the server refuses to merge into an
        // existing KPI rather than upserting over it.
        ...(edit.isNew ? { create_only: true } : {}),
        // '' clears it, which is why it is sent as a string and never as null —
        // null means "leave whatever is stored alone" to the server.
        department: (edit.department || '').trim(),
        objective: k.objective || '', formula: k.formula || '', criteria: k.criteria || '',
        field: edit.field ? edit.field.trim() : null,
        responsible: edit.responsible || 'hr',
        denominator_field: (edit.denominator_field ?? k.denominator_field) || null,
        uom: k.uom || '',
        direction: (edit.direction ?? k.direction) === 'lower' ? 'lower' : 'higher',
        // The ladder is now edited here rather than passed through untouched.
        // An empty one saves as null, which is what "no scoring rule" has
        // always meant to the engine — not an empty list it would walk.
        //
        // ON A PLANT SCOPE the shared ladder is sent back UNCHANGED and the
        // edited rungs go to the scoped endpoint below. Sending `edit.bands`
        // here instead would write one plant's ladder over the rule every
        // other plant is marked against — the exact mistake this split exists
        // to make impossible.
        bands: plantScope
          ? ((edit.sharedBands && edit.sharedBands.length) ? edit.sharedBands : null)
          : ((edit.bands && edit.bands.length) ? edit.bands : null),
        // A ladder derived from a reading is saved SAYING SO. Only sent when
        // the rungs are still exactly as built — edit one and it stops being a
        // derived ladder and becomes a decision, which is what `undefined`
        // means to the server ("leave the stamp alone").
        ...(builtUnchanged && !plantScope ? {
          bands_provisional: true,
          // Template literal rather than concatenating a separator: a sheet
          // NAME is built on the server and never here, and a guard test greps
          // this file for that concatenation to keep it so. This is a
          // provenance note, but it read identically to what the guard watches.
          bands_basis: `built from ${edit.built.period || 'the latest month'}`
            + (edit.built.unit ? ` · ${edit.built.unit}` : ''),
          bands_why: edit.built.why,
        } : {}),
        target: k.target ?? null, active: k.active !== false,
      });
      // One ladder, to exactly the plants named. The server writes one key per
      // selected plant and touches neither the shared ladder nor any plant that
      // was not named — and reports back which plants hold an override, so the
      // claim is checkable rather than merely stated.
      let storedByUnit = null;
      let storedShared;
      if (plantScope) {
        const r = await kpiApi.saveBandsForScope(edit.key, {
          scope: edit.scope, bands: edit.bands, mode: 'save',
        });
        storedByUnit = r.data?.bands_by_unit || {};
        storedShared = r.data?.shared_bands ?? null;
        // VERIFY, do not announce. The server returns what it stored, so the
        // claim "saved" can be checked against the database rather than against
        // the request that was sent.
        const want = JSON.stringify(edit.bands);
        const missed = edit.scope.filter(
          (p) => JSON.stringify(storedByUnit[p] || null) !== want);
        if (missed.length) {
          toast.error(`Consistency error: ${missed.join(', ')} did not store the ladder `
                      + 'that was sent. Nothing else was changed — please re-check '
                      + 'before relying on these marks.');
          return;
        }
        toast.success(`Ladder saved for ${edit.scope.join(' and ')}`
          + (edit.scope.length > 1 ? ' — each plant has its own copy' : ''));
      } else if (edit.isNew) {
        toast.success(`"${edit.name.trim()}" created as ${newKey}`
          + (edit.source === 'manual'
            ? ' — typed in by hand until a source field is chosen'
            : ((edit.bands || []).length ? '' : ' — add a marking ladder so it can score')));
      } else {
        toast.success('Shared ladder saved'
          + (Object.keys(edit.bandsByUnit || {}).length
            ? ' — plant-specific ladders were not changed' : ''));
      }
      // SAVING FINISHES THE EDIT. The panel returns to the read-only view, so
      // "saved" is something you can see rather than something you have to
      // infer from a form that still looks editable.
      //
      // It briefly did stay open, to stop a plant-scope save looking as though
      // the scope had reset. That was the wrong half of the problem to fix:
      // the real gap was that the read-only view never mentioned plant-specific
      // ladders, so a save to Unit 3 showed the shared ladder afterwards and
      // read as a no-op. The view names them now, and the editor can close.
      //
      // A NEWLY CREATED KPI is the exception and stays open, carrying its real
      // key — a new KPI has no source field and no ladder yet, and closing here
      // would mean finding it again in a list of 230 to add them.
      await reseedAfterSave(storedByUnit, storedShared, newKey);
      if (edit.isNew) setEdit((x) => x && ({ ...x, isNew: false, key: newKey }));
      else setEdit(null);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };
  // Map a KPI straight from its row, without opening the editor. Mapping is a
  // LIST job — you go down the KPIs deciding what each one reads — and making
  // that a dozen open/pick/save/close cycles is what made it feel slow.
  //
  // Saves on pick. Everything else on the record is passed through untouched,
  // so this writes one field and cannot quietly reset a ladder or a name.
  const [savingField, setSavingField] = useState('');
  // Which manual KPI is mid-conversion, and which software it is being pointed
  // at. Held per-row rather than globally so opening one does not disturb
  // whatever else is on screen.
  const [convertKey, setConvertKey] = useState('');
  const [convertSrc, setConvertSrc] = useState('ops');

  const SRC_KEY_TO_MASTER = { ops: 'operations', maint: 'maintenance', npd: 'npd', hr: 'hr' };

  // Manual -> automatic. One write: the source moves off "manual" and the field
  // is set in the same save, because a record carrying one without the other is
  // the state that reads as automatic and behaves as neither.
  const convertToAuto = async (k, srcKey, path) => {
    if (!path) return;
    setSavingField(k.key);
    try {
      const source = SRC_KEY_TO_MASTER[srcKey] || srcKey;
      await kpiApi.saveMaster({
        key: k.key, name: k.name, source,
        objective: k.objective || '', formula: k.formula || '', criteria: k.criteria || '',
        field: path.trim(),
        responsible: k.responsible === 'employee' ? 'hod' : (k.responsible || 'hr'),
        denominator_field: k.denominator_field || null,
        uom: k.uom || '', direction: k.direction || 'higher',
        bands: (k.bands && k.bands.length) ? k.bands : null,
        target: k.target ?? null, active: k.active !== false,
      });
      setConvertKey('');
      // A source change moves the KPI between groups, so the whole master is
      // re-read here rather than patched in place.
      const r = await kpiApi.master(); setMaster(r.data);
      toast.success(
        (k.bands && k.bands.length)
          ? `${k.name} now reads ${path} and scores automatically`
          : `${k.name} now reads ${path} — set its marking ladder to make it score`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not convert this KPI');
    } finally { setSavingField(''); }
  };
  const saveFieldInline = async (k, path) => {
    setSavingField(k.key);
    try {
      await kpiApi.saveMaster({
        key: k.key, name: k.name, source: k._g.source,
        objective: k.objective || '', formula: k.formula || '', criteria: k.criteria || '',
        field: path ? path.trim() : null,
        responsible: k.responsible === 'employee' ? 'hod' : (k.responsible || 'hr'),
        denominator_field: k.denominator_field || null,
        uom: k.uom || '', direction: k.direction || 'higher',
        bands: (k.bands && k.bands.length) ? k.bands : null,
        target: k.target ?? null, active: k.active !== false,
      });
      // Patch the row in place rather than refetching the whole master: a
      // reload here would collapse the group you are working down.
      setMaster((m) => !m ? m : ({
        ...m,
        sources: (m.sources || []).map((g) => ({
          ...g,
          kpis: (g.kpis || []).map((x) => x.key === k.key ? { ...x, field: path || null } : x),
        })),
      }));
      toast.success(path ? `${k.name} reads ${path}` : `${k.name} is no longer mapped`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save the mapping');
    } finally { setSavingField(''); }
  };

  /**
   * A KPI that reads a number every month but has no ladder to convert it into
   * a mark — the gap that actually breaks scoring.
   *
   * MANUAL KPIs ARE NOT COUNTED. A ladder turns a figure from the software into
   * marks; a manual KPI has no figure, because a person types the mark in
   * directly. Counting all 154 of them would report a permanent un-closeable
   * backlog of things that are working exactly as intended — a number that
   * cries wolf is worse than no number.
   *
   * Retired KPIs are excluded for the same reason: nothing scores them.
   */
  const noLadder = (k) => k.active !== false && k.mode === 'auto'
    && !(Array.isArray(k.bands) && k.bands.length);

  /** {total, missing} across the whole catalogue, ignoring the other filters —
   *  the point of the badge is to say how much is left overall. */
  const ladderTally = React.useMemo(() => {
    const auto = (masterFlat || []).filter((k) => k.active !== false && k.mode === 'auto');
    return { total: auto.length, missing: auto.filter(noLadder).length };
  }, [masterFlat]);

  /** How many KPIs are withdrawn. Counted off `masterAll`, the only list that
   *  still carries them — `masterFlat` drops retired by construction. */
  const retiredTally = React.useMemo(
    () => (masterAll || []).filter((k) => k.active === false).length, [masterAll]);

  const matchKpi = (k) => {
    const q = mq.trim().toLowerCase();
    // A retired KPI is withdrawn: it earns no marks, cannot be added to a
    // sheet, and has no business in this list. It is hidden whether you are
    // browsing or SEARCHING — surfacing it on search was the old rule, and it
    // meant typing three letters produced a row that looked live, sat next to
    // the live one, and could not be used for anything.
    //
    // Not hidden irrecoverably, though. "Show retired" below brings them back
    // deliberately, because a retired KPI still owns its key — it is what
    // blocks a new KPI from taking that name — so there has to be a way to see
    // one and re-activate it.
    if (!mRetired && k.active === false) return false;
    if (mDept && k.department !== mDept) return false;
    // Exactly what the badge counts — a software-owned KPI with no field
    // mapped yet. A manual KPI has no field either but is not "awaiting"
    // anything, so the same `kind === 'api'` test the server uses applies here.
    if (mAwaiting && !(k._g?.kind === 'api' && !k.field)) return false;
    // "No marking ladder" means exactly what the scorer means by it: no bands,
    // so `resolve_master_kpi` reads the number and withholds the mark.
    if (mNoLadder && !noLadder(k)) return false;
    if (!q) return true;
    // Search covers name, key, department, role and source field so one box is
    // enough — no separate "search by" selector to learn.
    return [k.name, k.key, k.department, k.objective, k.criteria, k.field,
            k.sourceLabel, ...(k.roles || [])]
      .filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
  };
  // Searching or filtering opens every group that still has rows: a match you
  // cannot see is the same as no match at all.
  const mFiltering = !!(mq.trim() || mDept || mAwaiting || mNoLadder);
  // Every tab with a KPI picker needs the master loaded, because the picker is
  // built from it. Templates was added here once for exactly this reason, and
  // "Everyone's KPIs" was missed — opening it first left `master` null, so
  // `masterFlat` was [] and "Pick a KPI from the master" offered nothing at
  // all, with no error to explain the empty list.
  //
  // Listed by tab name rather than "any tab", so the 222-KPI master is still
  // only fetched by the screens that actually need it.
  useEffect(() => {
    if (!['master', 'templates', 'everyone'].includes(tab) || master) return;
    kpiApi.master().then((r) => setMaster(r.data)).catch(() => toast.error('Failed to load KPI master'));
  }, [tab, master]);

  // Adds only the Plant Head rows that are missing, at weight 0 — existing rows,
  // weights, objectives and criteria are left exactly as they are.
  const addPlantHeadRows = async () => {
    setBusy(true);
    try {
      const r = await kpiApi.addPlantHeadRows({ template_name: sel, weight: 0 });
      toast.success(r.data.added.length
        ? `Added ${r.data.added.length} Plant Head KPI row(s) — weight total unchanged at ${r.data.weight_after}%`
        : 'All Plant Head KPI rows are already on this sheet');
      // Re-read from the fresh response; `templates` state is still stale here.
      const t = await kpiApi.templates();
      setTemplates(t.data || []);
      const fresh = (t.data || []).find((x) => x.name === sel);
      setItems(fresh ? (fresh.items || []).map((i) => ({ ...i })) : []);
      setRev(fresh ? (fresh.rev ?? 0) : null);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not add rows'); }
    finally { setBusy(false); }
  };

  // ---- allocation ----
  const [alloc, setAlloc] = useState(null);
  const [pick, setPick] = useState({});
  // Per-row escape hatch: widen the template list beyond this person's department.
  const [showAllTmpl, setShowAllTmpl] = useState({});
  const loadAlloc = useCallback(async () => {
    try { const r = await kpiApi.allocation({ fy, unit: activeUnit }); setAlloc(r.data); setPick({}); }
    catch (e) { toast.error('Failed to load allocation'); }
  }, [fy, activeUnit]);
  useEffect(() => { if (tab === 'allocation') loadAlloc(); }, [tab, loadAlloc]);

  // Click a column to sort, click again to reverse. Unassigned people sort to
  // the top of Status by default — they are the ones needing action.
  const [allocSort, setAllocSort] = useState({ col: 'name', dir: 1 });
  const sortBy = (col) => setAllocSort((s) =>
    s.col === col ? { col, dir: -s.dir } : { col, dir: 1 });
  // ---- allocation filters + bulk selection (search -> select -> action) ----
  const [aq, setAq] = useState('');
  const [aDept, setADept] = useState('');
  const [aDesig, setADesig] = useState('');
  const [aOnlyUnassigned, setAOnlyUnassigned] = useState(false);
  const [aOnlyMoved, setAOnlyMoved] = useState(false);
  // Manual work queue: who still owes a typed mark for the last finished month.
  const [work, setWork] = useState(null);
  const loadWork = useCallback(async () => {
    try {
      const m = lastCompletedPeriod();
      const r = await kpiApi.nextPending({ month: m.month, fy: m.fy, unit: activeUnit });
      setWork({ ...r.data, label: m.month });
    } catch (e) { /* non-fatal */ }
  }, [activeUnit]);
  useEffect(() => { if (tab === 'allocation') loadWork(); }, [tab, loadWork]);

  // One click from "18 entries pending" to actually typing the first one.
  const startManualEntry = () => {
    if (!work?.next) { toast.success('All required KPI entries are complete.'); return; }
    window.location.href = `/employees/${work.next.id}?tab=kpi`;
  };
  const [aSel, setASel] = useState(() => new Set());
  const [bulkTmpl, setBulkTmpl] = useState('');

  const allocDepts = useMemo(() =>
    [...new Set((alloc?.rows || []).map((r) => r.department).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })), [alloc]);
  // Designations narrow to the chosen department, so the second filter only ever
  // offers roles that actually exist inside the first.
  const allocDesigs = useMemo(() =>
    [...new Set((alloc?.rows || [])
      .filter((r) => !aDept || r.department === aDept)
      .map((r) => r.designation).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })), [alloc, aDept]);

  const allocRows = useMemo(() => {
    const q = aq.trim().toLowerCase();
    const rows = (alloc?.rows || []).filter((r) =>
      (!q || `${r.name} ${r.card_no || ''} ${r.designation || ''} ${r.department || ''}`
        .toLowerCase().includes(q))
      && (!aDept || r.department === aDept)
      && (!aDesig || r.designation === aDesig)
      && (!aOnlyUnassigned || !r.assigned)
      // Both chips filter to this one checkbox, so a deleted sheet is reachable
      // from the red warning as well as the blue one.
      && (!aOnlyMoved || r.role_changed || r.sheet_missing));
    const { col, dir } = allocSort;
    const val = (r) => {
      // Sort by how much is ON the sheet, not by a stored weight the engine
      // does not read. An empty sheet sorts to the top, which is the one case
      // worth looking at.
      if (col === 'status') return r.assigned ? (Number(r.count) || 0) : -1;
      return String(r[col] ?? '').toLowerCase();
    };
    return rows.sort((a, b) => {
      const x = val(a), y = val(b);
      if (x < y) return -dir;
      if (x > y) return dir;
      // Stable tie-break on name, so re-sorting the same column doesn't shuffle
      // rows that compare equal.
      return String(a.name || '').localeCompare(String(b.name || ''));
    });
  }, [alloc, allocSort, aq, aDept, aDesig, aOnlyUnassigned, aOnlyMoved]);

  // Selection is keyed on employee id, so it survives re-sorting and re-filtering
  // — but "select all" only ever takes what is currently visible.
  const visibleIds = useMemo(() => allocRows.map((r) => r.id), [allocRows]);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => aSel.has(id));
  const toggleAllVisible = () => setASel((prev) => {
    const n = new Set(prev);
    if (allVisibleSelected) visibleIds.forEach((id) => n.delete(id));
    else visibleIds.forEach((id) => n.add(id));
    return n;
  });
  const toggleOne = (id) => setASel((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const allocCounts = useMemo(() => {
    const rows = alloc?.rows || [];
    return {
      total: rows.length,
      allocated: rows.filter((r) => r.assigned).length,
      unassigned: rows.filter((r) => !r.assigned).length,
      // There is deliberately no "weights do not total 100" count any more.
      // The engine derives the weighting from the KPI COUNT — every KPI on a
      // sheet gets an equal share summing to exactly 100, whatever the stored
      // weights say — so a mismatched stored total could not change a single
      // mark. Warning about it sent HR to fix nine sheets that were scoring
      // correctly the whole time.
      //
      // A sheet with NO KPIs is the real version of that problem: nothing to
      // weight means nothing to score, and that one is worth showing.
      empty: rows.filter((r) => r.assigned && !r.count).length,
      // Their sheet is named for a role they are no longer in. In practice this
      // is usually a DEPARTMENT RENAME rather than a promotion — splitting
      // Quality into Quality Assurance / Quality Control / SQA / Standard Room
      // left everyone's sheet still called "Quality · …". They are still being
      // scored; the sheet just belongs to the old role.
      roleChanged: rows.filter((r) => r.role_changed && !r.sheet_missing).length,
      // Materially worse, and it used to be mixed in with the above: the sheet
      // they point at has been DELETED. The monthly job matches by sheet name,
      // so these people are never found and score nothing at all.
      sheetMissing: rows.filter((r) => r.sheet_missing).length,
    };
  }, [alloc]);

  // Role change is never applied silently: it changes how someone is measured
  // mid-year, so HR sees both sheets and confirms (§14).
  const reviewRoleChange = async (row) => {
    if (!window.confirm(
      `${row.name} — role changed\n\n`
      + `Now: ${row.department} · ${row.designation}\n\n`
      + `Currently on sheet : ${row.on_sheet}\n`
      + `Sheet for this role: ${row.suggested}\n\n`
      + `Move them to "${row.suggested}"?\n\n`
      + `Marks already recorded against a KPI that exists on both sheets are kept.`)) return;
    setBusy(true);
    try {
      await kpiApi.bulkAssign({ fy, employee_ids: [row.id], template_name: row.suggested });
      toast.success(`${row.name} moved to ${row.suggested}`);
      await loadAlloc();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not apply the new template'); }
    finally { setBusy(false); }
  };

  const bulkAssign = async () => {
    const ids = [...aSel];
    if (!ids.length) { toast.error('Select at least one employee'); return; }
    if (!bulkTmpl) { toast.error('Choose a template to assign'); return; }
    if (!window.confirm(
      `Assign "${bulkTmpl}" to ${ids.length} employee(s)?\n\n`
      + `Their KPI sheet will be re-cut from this template. Marks already recorded `
      + `against a KPI that stays on the sheet are kept.`)) return;
    setBusy(true);
    try {
      const r = await kpiApi.bulkAssign({ fy, employee_ids: ids, template_name: bulkTmpl });
      toast.success(`Assigned to ${r.data.applied} employee(s)`
        + (r.data.locked_skipped.length ? ` · ${r.data.locked_skipped.length} locked, skipped` : ''));
      setASel(new Set()); setBulkTmpl('');
      await loadAlloc();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Bulk assign failed'); }
    finally { setBusy(false); }
  };

  const applyOne = async (row) => {
    const name = pick[row.id] || row.suggested;
    if (!name) { toast.error('Pick a template'); return; }
    try { const r = await kpiApi.setDefinitions(row.id, { fy, template_name: name }); toast.success(`${row.name}: ${r.data.count} KPIs`); await loadAlloc(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Apply failed'); }
  };
  // Preview first: the same code path runs with dry_run, so what is shown and
  // what happens cannot describe different outcomes.
  const autoAll = async () => {
    setBusy(true);
    try {
      const { data: p } = await kpiApi.autoAllocate({ fy, only_unassigned: true, dry_run: true });
      if (!p.would_apply) {
        toast(p.no_suggestion.length
          ? `Nothing to allocate — ${p.no_suggestion.length} employee(s) have no matching template`
          : 'Everyone already has a sheet');
        return;
      }
      const byTmpl = {};
      p.plan.forEach((x) => { byTmpl[x.template] = (byTmpl[x.template] || 0) + 1; });
      const lines = Object.entries(byTmpl).sort().map(([t, n]) => `  ${t} → ${n}`).join('\n');
      if (!window.confirm(
        `Auto-allocate by department + designation?\n\n`
        + `Employees found: ${p.would_apply}\n`
        + `Already assigned: ${p.skipped_assigned}\n`
        + `Locked, skipped: ${p.locked_skipped}\n`
        + `No matching template: ${p.no_suggestion.length}\n\n`
        + `Templates to be used:\n${lines}`)) return;
      const r = await kpiApi.autoAllocate({ fy, only_unassigned: true });
      toast.success(`Allocated ${r.data.applied} employee(s)`
        + (r.data.no_suggestion.length ? ` · ${r.data.no_suggestion.length} need a manual pick` : ''));
      await loadAlloc();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Auto-allocate failed'); }
    finally { setBusy(false); }
  };

  // ---- review ----
  const [review, setReview] = useState(null);
  const loadReview = useCallback(async () => {
    try { const r = await kpiApi.review({ fy, unit: activeUnit }); setReview(r.data); }
    catch (e) { toast.error('Failed to load review'); }
  }, [fy, activeUnit]);
  useEffect(() => { if (tab === 'review') loadReview(); }, [tab, loadReview]);

  // ---- manual KPI: who is asked, and what HR is left holding ----
  // Read-only. Nothing here sends anything; the panel exists so the grouping can
  // be checked against reality before any request ever goes out.
  const [mkPrev, setMkPrev] = useState(null);
  const [mkQueue, setMkQueue] = useState(null);
  const [mkOpen, setMkOpen] = useState(false);
  const loadManual = useCallback(async () => {
    try {
      const [p, q] = await Promise.all([
        kpiApi.manualPreview({ unit: activeUnit }),
        kpiApi.manualHrQueue({ unit: activeUnit }),
      ]);
      setMkPrev(p.data); setMkQueue(q.data);
    } catch (e) { /* non-fatal — the review list still works without it */ }
  }, [activeUnit]);
  useEffect(() => { if (tab === 'review') loadManual(); }, [tab, loadManual]);

  // ---- automation status ----
  // Operations cannot rebuild tool-PM or negative-inventory state for a past
  // month, so this panel exists to make the daily capture visible: if it stops,
  // the month is lost silently unless someone can see it.
  const [sched, setSched] = useState(null);
  const loadSched = useCallback(async () => {
    try { const r = await kpiApi.schedule({ fy, unit: activeUnit }); setSched(r.data); }
    catch (e) { /* non-fatal — the review list still works without it */ }
  }, [fy, activeUnit]);
  useEffect(() => { if (['review', 'allocation'].includes(tab)) loadSched(); }, [tab, loadSched]);

  const publishMonth = async () => {
    if (!sched) return;
    const { fy: pfy, month: pm } = sched.previous_month;
    if (!window.confirm(`Publish ${pm} ${pfy} now and freeze it?\n\n`
      + `Every auto KPI for ${(sched.auto_units || []).join(' and ')} will be `
      + `computed and posted to employees' sheets. Once frozen the marks do not `
      + `change, even though Operations keeps recomputing live.`)) return;
    setBusy(true);
    try {
      const { data: d } = await kpiApi.runMonth({ fy: pfy, month: pm });
      if (d.skipped === 'no month-end capture') {
        // Refused on purpose: freezing a month we never captured would lock its
        // tool marks to today's live state. Offer the override explicitly.
        if (window.confirm(`${d.detail}\n\nPublish ${pm} anyway?\n\n`
          + `Its tool utilisation and negative inventory marks will reflect TODAY, `
          + `not ${pm} month-end, and will then be frozen. Every other KPI is `
          + `month-scoped and unaffected.`)) {
          const { data: f } = await kpiApi.runMonth({ fy: pfy, month: pm, force: true });
          toast.success(`${pm} published — ${f.written} marks across ${f.sheets.length} sheet(s)`);
        }
      } else if (d.skipped) {
        toast(`${pm} was already published on ${(d.frozen_at || '').slice(0, 10)}`);
      } else {
        toast.success(`${pm} published — ${d.written} marks across ${d.sheets.length} sheet(s)`);
      }
      await loadSched();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not publish the month'); }
    finally { setBusy(false); }
  };

  const captureNow = async () => {
    setBusy(true);
    try {
      const r = await kpiApi.captureNow();
      if (r.data.ok) toast.success(`Captured ${r.data.day} for ${r.data.units.join(', ')}`);
      else toast.error(r.data.error || 'Capture failed');
      await loadSched();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Capture failed'); }
    finally { setBusy(false); }
  };


  // ---- data sources (kept from the retired Auto KPI tab) --------------------
  // Operations and Maintenance months arrive as FILES uploaded in Source Data;
  // nothing here calls those apps. What remains is the supply
  // readout: which months each external source has supplied.
  const [autoStatus, setAutoStatus] = useState(null);
  const loadAutoStatus = useCallback(async () => {
    try { const r = await kpiApi.autoStatus(fy); setAutoStatus(r.data); }
    catch (e) { /* non-fatal: the master tab still works without it */ }
  }, [fy]);
  useEffect(() => { if (tab === 'master') loadAutoStatus(); }, [tab, loadAutoStatus]);

  // Delete one KPI from the master. Two steps on purpose: the cross only opens
  // the confirmation, and only [Delete] calls the server.
  const [delKpi, setDelKpi] = useState(null);      // the KPI awaiting confirmation
  // What pressing delete would actually cost, read before the box can be
  // confirmed — the button says "Delete" or "Retire this KPI" depending on it,
  // so it must not be guessable from the client.
  const [delImpact, setDelImpact] = useState(null);
  const askDeleteKpi = async (k) => {
    setDelKpi(k); setDelImpact(null);
    try {
      const r = await kpiApi.deleteImpact(k.key);
      setDelImpact(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not check what this KPI affects');
      setDelKpi(null);
    }
  };
  const [delBusy, setDelBusy] = useState(false);
  const confirmDeleteKpi = async () => {
    if (!delKpi) return;
    setDelBusy(true);
    // ONLY the delete is inside this try. It used to also wrap the refresh that
    // follows, and that refresh called `loadMaster` — a function that does not
    // exist in this file. So every delete threw a ReferenceError AFTER the
    // server had already done the work, the catch reported it as "Could not
    // delete this KPI", and the list never refreshed: the KPI was gone, the
    // screen said it had failed, and only a reload showed the truth.
    //
    // A failure to re-read the list is not a failure to delete, and must never
    // again be reported as one.
    let res;
    try {
      // Identified by the stable key, never the display name.
      res = await kpiApi.deleteMaster(delKpi.key);
    } catch (e) {
      // A dropped connection carries no reason, so say what to do about it
      // rather than implying the server refused.
      toast.error(e?.response?.data?.detail
        || 'The server did not respond — it may have been restarting. Refresh and '
         + 'check whether the KPI is still listed before trying again.',
        { duration: 9000 });
      setDelBusy(false);
      return;
    }
    // The server decides which of the two happened, and says so in words.
    toast.success(res.data.message || `${res.data.name || delKpi.key} deleted`,
                  { duration: res.data.retired ? 9000 : 4000 });
    setDelKpi(null); setDelImpact(null);
    try {
      const r = await kpiApi.master(); setMaster(r.data);   // the row disappears at once
      const t = await kpiApi.templates(); setTemplates(t.data || []);
    } catch {
      toast.warning('Deleted. The list could not be refreshed — reload to see it.');
    } finally { setDelBusy(false); }
  };

  return (
    <div className="flex h-screen app-shell">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />
        <div className="flex-1 overflow-auto p-5 sm:p-6 page-enter">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-[-0.03em] text-[#172554]">KPI Center</h1>
              <p className="text-sm text-[#5A6377]">Templates, allocation and review · FY {fy}{activeUnit ? ` · ${activeUnit}` : ''}</p>
            </div>
            <Link to="/kpi-qr-sheet" data-testid="kpi-qr-sheet-link"
              className="btn-grad inline-flex items-center gap-2 text-sm font-semibold text-white rounded-xl px-4 py-2.5">
              Print QR sheets
            </Link>
          </div>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              {/* Ordered the way the work flows, not the way the data flows:
                  you allocate people to sheets, sheets are built from templates,
                  and templates pick their rows from the master. */}
              <TabsTrigger value="allocation">Allocation</TabsTrigger>
              <TabsTrigger value="templates">Templates</TabsTrigger>
              <TabsTrigger value="master" data-testid="kpi-master-tab">KPI Master</TabsTrigger>
              {/* The numbers a KPI can be pointed at. It sat on its own page,
                  where it read as a reference list nobody had a reason to open;
                  it exists for the KPIs beside it, so it belongs here. */}
              {/* Categories and Criteria are deliberately NOT tabs here. Both
                  screens still exist (CategoriesTab.js, CriteriaTab.js) and so
                  does everything behind them — `/api/kpi-categories`,
                  `/api/kpi-routes`, the `kpi_routes` table and the route-driven
                  monthly compute are all untouched and still running. Only the
                  way in was removed. Restoring either is this line plus its
                  TabsContent below. */}
              <TabsTrigger value="source" data-testid="kpi-source-tab">Source Data</TabsTrigger>
              {/* After Source Data, because it reads in the order the work
                  happens: the numbers arrive, they become KPIs, the KPIs go on
                  sheets — and these are the ones that go on EVERY sheet. */}
              <TabsTrigger value="everyone" data-testid="kpi-everyone-tab">
                Everyone&rsquo;s KPIs
              </TabsTrigger>
            </TabsList>

            {/* ---------------- Allocation ---------------- */}
            <TabsContent value="allocation" className="mt-4">
              <div className="bg-white border border-[#E6E9F0] rounded-xl p-5">
                {/* "What do I need to do now?" — a short list of real, current
                    work. Each line filters or opens the screen that fixes it,
                    rather than being another dashboard to read (§24). */}
                {alloc && (
                  <div className="mb-4 rounded-lg border border-[#E6E9F0] bg-[#FBFCFE] p-3">
                    <div className="flex items-baseline justify-between flex-wrap gap-2 mb-1.5">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-[#98A1B2]">
                        My KPI work
                      </p>
                      <p className="text-[11px] text-[#98A1B2]">
                        {allocCounts.total} employees · {allocCounts.allocated} with a sheet ·{' '}
                        {(alloc?.rows || []).reduce((n, r) => n + (r.count || 0), 0)} KPI rows
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2 text-[13px]">
                      {work && work.pending_count > 0 && (
                        <button onClick={startManualEntry} data-testid="work-manual-entries"
                          className="px-3 py-1.5 rounded-lg bg-white border border-[#FBD9A8] text-[#8A5A12] hover:bg-[#FFF6E8] font-medium">
                          🟠 {work.pending_count} employee(s) owe manual {work.label} marks
                          <span className="font-normal"> — start entry →</span>
                        </button>
                      )}
                      {work && work.pending_count === 0 && (
                        <span className="px-3 py-1.5 rounded-lg bg-[#EAF7F0] border border-[#CDEBDB] text-[#15654B]">
                          🟢 All manual {work.label} marks are in
                        </span>
                      )}
                      {allocCounts.unassigned > 0 && (
                        <button onClick={() => { setAOnlyUnassigned(true); setADept(''); setADesig(''); setAq(''); }}
                          data-testid="work-unassigned"
                          className="px-3 py-1.5 rounded-lg bg-white border border-[#FBD9A8] text-[#B14A0E] hover:bg-[#FFF6E8]">
                          {allocCounts.unassigned} employee(s) with no KPI sheet →
                        </button>
                      )}
                      {allocCounts.empty > 0 && (
                        <button onClick={() => { setAOnlyUnassigned(false); setAq(''); setADept(''); setADesig(''); sortBy('status'); }}
                          data-testid="work-empty-sheets"
                          className="px-3 py-1.5 rounded-lg bg-white border border-[#FDECEC] text-[#C2151F] hover:bg-[#FFF7F7]">
                          {allocCounts.empty} sheet(s) with no KPIs on them →
                        </button>
                      )}
                      {allocCounts.sheetMissing > 0 && (
                        <button onClick={() => { setAOnlyMoved(true); setAOnlyUnassigned(false); setAq(''); setADept(''); setADesig(''); }}
                          data-testid="work-sheet-missing"
                          title="The KPI sheet these employees point at has been deleted. The monthly job finds employees by sheet name, so they are never found and score nothing."
                          className="px-3 py-1.5 rounded-lg bg-white border border-[#FDECEC] text-[#C2151F] hover:bg-[#FFF7F7] font-medium">
                          🔴 {allocCounts.sheetMissing} employee(s) on a deleted sheet — scoring nothing →
                        </button>
                      )}
                      {allocCounts.roleChanged > 0 && (
                        <button onClick={() => { setAOnlyMoved(true); setAOnlyUnassigned(false); setAq(''); setADept(''); setADesig(''); }}
                          data-testid="work-role-changed"
                          title="Their department or designation changed — usually a department being renamed or split. They are still scored normally on the sheet they hold; it is just named for the old role. Moving them is a decision about how they are measured, so it is never done automatically."
                          className="px-3 py-1.5 rounded-lg bg-white border border-[#D7E3FF] text-[#2563EB] hover:bg-[#EEF3FF]">
                          {allocCounts.roleChanged} on a sheet named for their old role →
                        </button>
                      )}
                      {sched && !sched.previous_month_published && (
                        <button onClick={() => setTab('review')}
                          className="px-3 py-1.5 rounded-lg bg-white border border-[#D7E3FF] text-[#2563EB] hover:bg-[#EEF3FF]">
                          {sched.previous_month.month} not worked out yet →
                        </button>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <h2 className="font-display text-lg font-bold tracking-[-0.015em] text-[#172554]">Allocate KPIs</h2>
                  <Button onClick={autoAll} disabled={busy}>Auto-allocate unassigned</Button>
                </div>

                {/* Compact clickable counts. Clicking one filters the table below
                    rather than opening another screen. */}
                {alloc && (
                  <div className="flex flex-wrap items-center gap-2 mb-3 text-[13px]">
                    <button onClick={() => { setAOnlyUnassigned(false); setADept(''); setADesig(''); setAq(''); }}
                      className="px-3 py-1.5 rounded-lg border border-[#E6E9F0] hover:bg-[#F7F8FB]">
                      <b className="text-[#11182B]">{allocCounts.total}</b> <span className="text-[#5A6377]">employees</span>
                    </button>
                    <button onClick={() => setAOnlyUnassigned(false)}
                      className={`px-3 py-1.5 rounded-lg border ${!aOnlyUnassigned ? 'border-[#CDEBDB] bg-[#EAF7F0]' : 'border-[#E6E9F0] hover:bg-[#F7F8FB]'}`}>
                      <b className="text-[#15654B]">{allocCounts.allocated}</b> <span className="text-[#5A6377]">allocated</span>
                    </button>
                    <button onClick={() => setAOnlyUnassigned(true)} data-testid="alloc-filter-unassigned"
                      className={`px-3 py-1.5 rounded-lg border ${aOnlyUnassigned ? 'border-[#FBD9A8] bg-[#FFF6E8]' : 'border-[#E6E9F0] hover:bg-[#F7F8FB]'}`}>
                      <b className={allocCounts.unassigned ? 'text-[#B14A0E]' : 'text-[#98A1B2]'}>{allocCounts.unassigned}</b>{' '}
                      <span className="text-[#5A6377]">unassigned</span>
                    </button>
                    {allocCounts.empty > 0 && (
                      <span className="px-3 py-1.5 rounded-lg border border-[#FDECEC] bg-[#FFF7F7]"
                        title="Has a sheet, but the sheet carries no KPIs — so there is nothing to score">
                        <b className="text-[#C2151F]">{allocCounts.empty}</b> <span className="text-[#5A6377]">empty</span>
                      </span>
                    )}
                  </div>
                )}

                {/* Search -> select -> action */}
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <Input value={aq} onChange={(e) => setAq(e.target.value)} data-testid="alloc-search"
                    placeholder="Search name, card no, designation…" className="max-w-xs text-sm" />
                  <select value={aDept} onChange={(e) => { setADept(e.target.value); setADesig(''); }}
                    data-testid="alloc-dept"
                    className="border border-[#E6E9F0] rounded-lg px-2 py-1.5 text-sm text-[#5A6377]">
                    <option value="">All departments</option>
                    {allocDepts.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <select value={aDesig} onChange={(e) => setADesig(e.target.value)} data-testid="alloc-desig"
                    className="border border-[#E6E9F0] rounded-lg px-2 py-1.5 text-sm text-[#5A6377]">
                    <option value="">All designations</option>
                    {allocDesigs.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <label className="flex items-center gap-1.5 text-xs text-[#5A6377] cursor-pointer">
                    <input type="checkbox" checked={aOnlyUnassigned}
                      onChange={(e) => setAOnlyUnassigned(e.target.checked)} />
                    Only unassigned
                  </label>
                  {(allocCounts.roleChanged > 0 || allocCounts.sheetMissing > 0) && (
                    <label className="flex items-center gap-1.5 text-xs text-[#5A6377] cursor-pointer">
                      <input type="checkbox" checked={aOnlyMoved}
                        onChange={(e) => setAOnlyMoved(e.target.checked)} />
                      Sheet doesn't match role
                    </label>
                  )}
                  <span className="text-[11px] text-[#98A1B2] ml-auto">
                    {allocRows.length} shown{aSel.size ? ` · ${aSel.size} selected` : ''}
                  </span>
                </div>

                {/* Bulk bar appears only when something is selected, so it never
                    sits there inviting an accidental mass change. */}
                {aSel.size > 0 && (
                  <div className="flex flex-wrap items-center gap-2 mb-3 p-3 rounded-lg bg-[#EEF3FF] border border-[#D7E3FF]">
                    <span className="text-[13px] font-semibold text-[#172554]">{aSel.size} selected</span>
                    <select value={bulkTmpl} onChange={(e) => setBulkTmpl(e.target.value)}
                      data-testid="alloc-bulk-template"
                      className="border border-[#E6E9F0] rounded-lg px-2 py-1.5 text-sm bg-white">
                      <option value="">Choose a template…</option>
                      {(alloc?.templates || []).map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <Button size="sm" onClick={bulkAssign} disabled={busy || !bulkTmpl}
                      data-testid="alloc-bulk-apply">Assign to {aSel.size}</Button>
                    <button onClick={() => setASel(new Set())}
                      className="text-[12px] text-[#5A6377] hover:underline">Clear selection</button>
                  </div>
                )}
                {!alloc ? <p className="text-[#98A1B2]">Loading…</p> : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-[#EFF1F6] text-[#5A6377] text-xs uppercase">
                        <tr>
                          <th className="py-2 px-3 w-8">
                            <input type="checkbox" checked={allVisibleSelected}
                              onChange={toggleAllVisible} data-testid="alloc-select-all"
                              title="Select all visible" />
                          </th>
                          {ALLOC_COLS.map(([col, label]) => (
                            <th key={col} className="py-2 px-3 text-left">
                              <button onClick={() => sortBy(col)} data-testid={`alloc-sort-${col}`}
                                className="uppercase inline-flex items-center gap-1 hover:text-[#2563EB]">
                                {label}
                                <span className="text-[9px] text-[#98A1B2]">
                                  {allocSort.col === col ? (allocSort.dir === 1 ? '▲' : '▼') : '⇅'}
                                </span>
                              </button>
                            </th>
                          ))}
                          <th className="py-2 px-3 text-left">Template</th><th className="py-2 px-3"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {allocRows.map((r) => (
                          <tr key={r.id} className={`border-b border-[#F2F4F8] ${aSel.has(r.id) ? 'bg-[#F5F8FF]' : ''}`}>
                            <td className="py-2 px-3">
                              <input type="checkbox" checked={aSel.has(r.id)}
                                onChange={() => toggleOne(r.id)} data-testid={`alloc-pick-${r.id}`} />
                            </td>
                            <td className="py-2 px-3 text-[#11182B]">
                              {r.name}
                              {/* Still employed, so still allocatable and still
                                  owed marks for the months they worked — but
                                  anyone choosing a sheet should know the seat
                                  empties on a date. They drop off this list on
                                  their own once that day has passed. */}
                              {r.employment_state === 'On Notice Period' && (
                                <span className="ml-1.5 text-[10px] font-semibold text-[#B14A0E]
                                                 bg-[#FFF6E8] border border-[#FBD9A8] rounded px-1.5 py-0.5"
                                  title={`Serving notice — last working day ${r.last_working_day || 'not set'}`}>
                                  leaving {r.last_working_day || ''}
                                </span>
                              )}
                            </td>
                            <td className="py-2 px-3 text-[#5A6377]">{r.department}</td>
                            <td className="py-2 px-3 text-[#5A6377]">
                              {r.designation}
                              {/* A deleted sheet is a different problem from an
                                  out-of-date one: the first scores nothing, the
                                  second scores fine on the wrong role's rows. */}
                              {r.sheet_missing ? (
                                <button onClick={() => reviewRoleChange(r)} disabled={busy}
                                  data-testid={`sheet-missing-${r.id}`}
                                  className="block text-[10px] text-[#C2151F] font-semibold underline text-left"
                                  title={`The sheet "${r.on_sheet}" no longer exists, so the monthly job never finds them. They score nothing until they are moved.`}>
                                  sheet deleted — scoring nothing →
                                </button>
                              ) : r.role_changed && (
                                <button onClick={() => reviewRoleChange(r)} disabled={busy}
                                  data-testid={`role-change-${r.id}`}
                                  className="block text-[10px] text-[#B14A0E] underline hover:text-[#C2151F] text-left"
                                  title={`Scored normally on "${r.on_sheet}", but their role is now "${r.suggested}"`}>
                                  sheet named for old role — review →
                                </button>
                              )}
                            </td>
                            <td className="py-2 px-3">
                              {/* Status opens that employee's sheet — the thing you
                                  almost always want next after reading it. */}
                              {/* The only thing that can go wrong here is an
                                  EMPTY sheet. The share each KPI carries is
                                  derived from the count, so a sheet with any
                                  KPIs on it always totals 100 — showing a
                                  stored percentage that disagreed was the
                                  screen inventing a problem. */}
                              {r.assigned
                                ? <>
                                  <Link to={`/employees/${r.id}?tab=kpi`}
                                    className={`pill ${r.count ? 'pill-success' : 'pill-warn'} hover:underline`}
                                    title={r.count
                                      ? `${r.count} KPIs, each worth ${(100 / r.count).toFixed(1)}% of this sheet`
                                      : 'This sheet has no KPIs on it, so nothing can be scored'}>
                                    {r.count ? `${r.count} KPIs · 100%` : 'no KPIs'}{r.locked ? ' · locked' : ''}
                                  </Link>
                                  {/* How the sheet gets its numbers, under the
                                      pill rather than in a column of its own.
                                      This is the difference between a sheet
                                      that looks after itself and one that costs
                                      somebody a number every month. */}
                                  {!!r.count && r.modes && (
                                    <div className="mt-0.5 text-[10px] leading-tight">
                                      {r.modes.auto > 0 && (
                                        <span className="text-[#2563EB] font-semibold"
                                          title="Filled by the software — nobody has to do anything each month">
                                          {r.modes.auto} auto
                                        </span>
                                      )}
                                      {r.modes.auto > 0 && r.modes.manual > 0 && <span className="text-[#C9CFDC]"> · </span>}
                                      {r.modes.manual > 0 && (
                                        <span className="text-[#8A5A12]"
                                          title="Somebody types this in every month">
                                          {r.modes.manual} manual
                                        </span>
                                      )}
                                      {r.modes.unmapped > 0 && (
                                        <>
                                          <span className="text-[#C9CFDC]"> · </span>
                                          {/* Not "manual" — a row pointing at no KPI in
                                              the master, which nobody can fill at all. */}
                                          <span className="text-[#C2151F] font-semibold"
                                            title="Not linked to any KPI in the master, so it can never be filled — by the software or by hand">
                                            {r.modes.unmapped} not linked
                                          </span>
                                        </>
                                      )}
                                    </div>
                                  )}
                                </>
                                : <span className="text-xs text-[#98A1B2]">unassigned</span>}
                            </td>
                            <td className="py-2 px-3">
                              {/* Their own department's sheets only. Offering all
                                  104 to an Accounts clerk is how the wrong sheet
                                  gets picked; "all departments" stays available
                                  for the genuine exception. */}
                              <select value={pick[r.id] ?? r.suggested ?? ''} onChange={(e) => setPick((s) => ({ ...s, [r.id]: e.target.value }))}
                                className="border border-[#E6E9F0] rounded-lg px-2 py-1 text-sm">
                                <option value="">— pick —</option>
                                {((showAllTmpl[r.id] ? alloc.templates : r.templates) || []).map((t) => (
                                  <option key={t} value={t}>{t}{t === r.suggested ? ' (suggested)' : ''}</option>
                                ))}
                              </select>
                              {!showAllTmpl[r.id] && (alloc.templates || []).length > (r.templates || []).length && (
                                <button onClick={() => setShowAllTmpl((s) => ({ ...s, [r.id]: true }))}
                                  className="ml-2 text-[11px] text-[#2563EB] hover:underline">
                                  all departments
                                </button>
                              )}
                            </td>
                            <td className="py-2 px-3 text-right">
                              <Button size="sm" variant="outline" disabled={r.locked} onClick={() => applyOne(r)}>{r.assigned ? 'Reassign' : 'Apply'}</Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* ---------------- Templates ---------------- */}
            {/* ---------------- Global KPI Master ---------------- */}
            {/* ---------------- KPI Master ----------------
                List on the left, one KPI on the right. The screen this
                replaced put eight columns on every one of 229 rows — the
                dotted source path and the internal key among them — so
                choosing a KPI meant reading past the two things that never
                help you choose. Nothing was deleted: the key, the mapping and
                the per-plant targets all moved into Advanced on the right. */}
            <TabsContent value="master" className="mt-4">
              <div className="flex items-baseline gap-2 mb-3 flex-wrap">
                <h3 className="font-medium text-[#11182B]">KPI Master</h3>
                <p className="text-sm text-[#5A6377]">Manage KPIs and how they are scored.</p>
                {/* The paragraph that used to sit here explained that a
                    template points at a KPI by its key. True, and worth
                    knowing once — not worth 54 words above every visit. */}
                <span className="text-[#C9CFDC] cursor-help text-xs"
                  data-testid="kpi-master-help"
                  title={'A template row points at a KPI by its key, so renaming a KPI never breaks '
                       + 'its data mapping. Two systems can report similar-sounding numbers for '
                       + 'different things — Operations owns tooling, Machine Maintenance owns '
                       + 'machines — so the owner is declared per KPI.'}>ⓘ</span>
                {/* Creating a KPI had no way in. The API has always accepted
                    one, but the only routes into the master were the code seed
                    and the template importer — so a KPI the business wanted and
                    no sheet mentioned could not be made at all. */}
                <Button size="sm" variant="outline" className="ml-auto"
                  data-testid="kpi-master-new" onClick={newKpi}>+ New KPI</Button>
                <span className="text-[11px] text-[#98A1B2]">
                  {(master?.sources || []).reduce((n, g) => n + (g.kpis || []).length, 0)} KPIs
                </span>
              </div>

              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <Input value={mq} onChange={(e) => setMq(e.target.value)} className="max-w-xs text-sm"
                  placeholder="Search KPI…" data-testid="kpi-master-search" />
                <select value={mDept} onChange={(e) => setMDept(e.target.value)}
                  className="border border-[#E6E9F0] rounded-lg px-2 py-1.5 text-sm text-[#5A6377]">
                  <option value="">All departments</option>
                  {masterDepts.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
                {/* Ladder coverage, and a way straight to the gap. A KPI with
                    no ladder reads its number every month and then withholds
                    the mark, which is invisible until somebody opens it — so
                    the count is on the toolbar and clicking it filters to
                    exactly those. Reads "all scored" when the gap is closed,
                    because a zero deserves saying. */}
                <button onClick={() => setMNoLadder((v) => !v)}
                  data-testid="kpi-master-no-ladder"
                  aria-pressed={mNoLadder}
                  title={ladderTally.missing
                    ? `${ladderTally.missing} of ${ladderTally.total} automatic KPIs have no marking ladder — they read a number every month and then withhold the mark. Click to list them. Typed-in KPIs are not counted: a person enters their mark directly, so they need no ladder.`
                    : `All ${ladderTally.total} automatic KPIs have a marking ladder. Typed-in KPIs are not counted — a person enters their mark directly.`}
                  className={`px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                    mNoLadder
                      ? 'bg-[#B14A0E] border-[#B14A0E] text-white'
                      : ladderTally.missing
                        ? 'bg-[#FFF6E8] border-[#FBD9A8] text-[#B14A0E] hover:bg-[#FDEFD9]'
                        : 'bg-[#ECFDF5] border-[#A7F3D0] text-[#047857]'}`}>
                  {ladderTally.missing
                    ? `${ladderTally.missing} without a ladder`
                    : `All ${ladderTally.total} scored ✓`}
                </button>
                {mNoLadder && (
                  <button onClick={() => setMNoLadder(false)}
                    className="text-[11px] text-[#2563EB] hover:underline">clear</button>
                )}
                {/* Withdrawn KPIs, off by default. Counted so the number is
                    visible without turning them on — "23 retired" answers the
                    question most people have, which is whether any exist. */}
                {!!retiredTally && (
                  <button onClick={() => setMRetired((v) => !v)}
                    data-testid="kpi-master-show-retired"
                    aria-pressed={mRetired}
                    title={mRetired
                      ? 'Hide the withdrawn KPIs again'
                      : 'Show withdrawn KPIs. They earn no marks and cannot be added '
                        + 'to a sheet, but each still owns its name.'}
                    className={`text-[11px] rounded-full px-2.5 py-1 border transition-colors ${
                      mRetired
                        ? 'bg-[#5A6377] border-[#5A6377] text-white'
                        : 'bg-white border-[#E6E9F0] text-[#5A6377] hover:border-[#5A6377]'}`}>
                    {mRetired ? `hiding ${retiredTally} retired` : `${retiredTally} retired`}
                  </button>
                )}
                <div className="flex items-center gap-1 text-xs ml-auto">
                  <span className="text-[#98A1B2]">Group by</span>
                  {[['department', 'Department'], ['source', 'Software']].map(([v, lbl]) => (
                    <button key={v} onClick={() => setMGroupBy(v)}
                      data-testid={`kpi-master-groupby-${v}`}
                      className={`px-2 py-1 rounded-md border ${mGroupBy === v
                        ? 'bg-[#EEF3FF] border-[#D7E3FF] text-[#2563EB] font-semibold'
                        : 'bg-white border-[#E6E9F0] text-[#5A6377]'}`}>
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid gap-4 lg:grid-cols-[minmax(280px,380px)_1fr] items-start">
                {/* ---- left: the list. Name, department, how it is filled. ---- */}
                <div className="bg-white border border-[#E6E9F0] rounded-xl overflow-hidden">
                  <div className="max-h-[70vh] overflow-y-auto">
                    {masterGroups.map((grp) => {
                      const rows = (grp.rows || []).filter(matchKpi);
                      if (!rows.length) return null;
                      const open = mFiltering || openGroups.has(grp.id);
                      return (
                      <div key={grp.id}>
                        <button type="button" onClick={() => toggleGroup(grp.id)}
                          disabled={mFiltering}
                          data-testid={`kpi-master-group-${grp.id}`}
                          aria-expanded={open}
                          className="w-full flex items-center gap-2 text-left px-3 py-2 bg-[#FAFBFD] border-b border-[#F2F4F8] hover:bg-[#F5F8FF] disabled:hover:bg-[#FAFBFD] sticky top-0 z-10">
                          <span className={`text-[#98A1B2] text-[10px] transition-transform ${open ? 'rotate-90' : ''}`}>▶</span>
                          <span className="font-medium text-[#172554] text-[13px] truncate">{grp.label}</span>
                          <span className="text-[11px] text-[#98A1B2] ml-auto">{rows.length}</span>
                          {grp.pending > 0 && (
                            <span role="button" tabIndex={0}
                              data-testid={`kpi-awaiting-${grp.id}`}
                              title={mAwaiting
                                ? 'Showing only KPIs with no number mapped — click to clear'
                                : `Show only the ${grp.pending} KPI(s) with no number mapped yet`}
                              onClick={(e) => { e.stopPropagation(); setMAwaiting((v) => !v); }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault(); e.stopPropagation(); setMAwaiting((v) => !v);
                                }
                              }}
                              className={`pill cursor-pointer text-[10px] ${mAwaiting
                                ? 'pill-warn ring-2 ring-[#FBD9A8]' : 'pill-warn hover:brightness-95'}`}>
                              {grp.pending}{mAwaiting ? ' ✕' : ''}
                            </span>
                          )}
                        </button>
                        {open && rows.map((k, ri) => {
                          const auto = k.mode !== 'manual';
                          const sel = preview?.key === k.key;
                          return (
                            <button key={k.key} onClick={() => setPreview(k)}
                              data-testid={`kpi-row-${k.key}`}
                              // Entrance staggers down the group, capped at 10
                              // rows' worth so a 60-KPI department does not
                              // spend two seconds arriving.
                              style={{ '--d': `${Math.min(ri, 10) * 28}ms`,
                                       '--kpi-grad': kpiLook(k).grad }}
                              className={`kpi-row enter-up relative w-full text-left pl-3 pr-3 py-2 border-b border-[#F7F8FB] last:border-0 flex items-center gap-2.5 ${
                                sel ? 'kpi-row-active bg-[#F5F8FF]' : 'hover:bg-[#FAFBFD]'}`}>
                              <KpiIcon kpi={k} size={32} solid={sel} />
                              <span className="min-w-0 flex-1">
                                <span className="block text-[13px] text-[#11182B] truncate">{k.name}</span>
                                <span className="block text-[11px] text-[#98A1B2] truncate">
                                  {k.department || 'No department'}
                                  {k.active === false && <span className="text-[#C2151F]"> · Retired</span>}
                                </span>
                              </span>
                              <span className={`text-[10px] font-semibold rounded px-1.5 py-0.5 shrink-0 ${
                                auto ? 'text-[#2563EB] bg-[#E8EFFD]' : 'text-[#8A5A12] bg-[#FFF6E8]'}`}>
                                {auto ? 'AUTO' : 'MANUAL'}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                      );
                    })}

                    {mFiltering && !masterGroups.some((g) => (g.rows || []).some(matchKpi)) && (
                      <div className="p-6 text-center" data-testid="kpi-master-no-match">
                        <p className="text-sm text-[#5A6377] mb-3">
                          No KPI matches the current filters.
                          {mAwaiting && ' Every KPI the software owns already has a number mapped.'}
                        </p>
                        <Button size="sm" variant="outline" data-testid="kpi-master-clear-filters"
                          onClick={() => { setMq(''); setMDept(''); setMAwaiting(false); }}>
                          Clear filters
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                {/* ---- right: the selected KPI, or the editor for it ---- */}
                <div className="bg-white border border-[#E6E9F0] rounded-xl min-h-[300px]">
                  {edit ? (
                    <div className="p-4">{renderEditPanel()}</div>
                  ) : (
                    <KpiMasterPanel
                      kpi={preview}
                      group={preview?._g}
                      roles={preview?.roles}
                      bandsText={bandRows(preview?.bands)}
                      onEdit={() => preview && openEdit(preview, preview._g)}
                      onEditLadder={() => preview && openEdit(preview, preview._g)}
                      onDelete={() => preview && askDeleteKpi(preview)}
                      unitTargets={preview && targetKpis.has(preview.key)
                        ? <UnitTargets only={preview.key} /> : null}
                    />
                  )}
                </div>
              </div>
            </TabsContent>

            {delKpi && (
              <div className="fixed inset-0 z-50 bg-black/30 flex items-start justify-center pt-28 px-4"
                onClick={() => !delBusy && (setDelKpi(null), setDelImpact(null))}>
                <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-5"
                  data-testid="kpi-delete-modal" onClick={(e) => e.stopPropagation()}>
                  {/* Two different outcomes, so the box says which one this is
                      BEFORE it happens. A KPI nobody has been marked on is
                      removed outright; one with marks is retired, because
                      pulling a scored row off a sheet re-weights the months it
                      was part of and silently rewrites a settled result. */}
                  <div className="font-semibold text-[#172554]">
                    {!delImpact ? 'Delete this KPI?'
                      : delImpact.will_retire ? 'This KPI has already been scored'
                      : 'Delete this KPI permanently?'}
                  </div>
                  <div className="text-[12px] text-[#11182B] mt-2">
                    {delKpi.name}
                    <span className="ml-1.5 font-mono text-[11px] text-[#98A1B2]">{delKpi.key}</span>
                  </div>
                  {!delImpact ? (
                    <p className="text-[13px] text-[#5A6377] mt-2">Checking what this would affect…</p>
                  ) : delImpact.will_retire ? (
                    <div className="mt-2 text-[13px] text-[#5A6377] space-y-2">
                      <p>
                        It carries <b>{delImpact.marks} recorded mark{delImpact.marks === 1 ? '' : 's'}</b>
                        {delImpact.employee_sheets > 0 && <> across <b>{delImpact.employee_sheets}</b> live sheet{delImpact.employee_sheets === 1 ? '' : 's'}</>}
                        {delImpact.archived_sheets > 0 && <> and <b>{delImpact.archived_sheets}</b> archived one{delImpact.archived_sheets === 1 ? '' : 's'}</>}.
                      </p>
                      <p className="rounded-lg bg-[#F0F7FF] border border-[#D7E3FF] p-2 text-[#172554]">
                        It will be <b>retired instead of deleted</b>. Every percentage already
                        calculated stays exactly as it is, the marks are kept, and it will not
                        appear on any new sheet. In a breakdown it reads as a deleted KPI.
                      </p>
                      {delImpact.templates > 0 && (
                        <p className="text-[12px]">
                          Removed from {delImpact.templates} role template{delImpact.templates === 1 ? '' : 's'}.
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-[13px] text-[#5A6377] mt-2">
                      Nobody has ever been marked on this KPI, so there is no history to keep.
                      It and its related records will be removed completely.
                    </p>
                  )}
                  <div className="flex justify-end gap-2 mt-5">
                    <Button size="sm" variant="outline" disabled={delBusy}
                      data-testid="kpi-delete-cancel"
                      onClick={() => { setDelKpi(null); setDelImpact(null); }}>Cancel</Button>
                    <Button size="sm" disabled={delBusy || !delImpact}
                      data-testid="kpi-delete-confirm"
                      className={delImpact?.will_retire
                        ? 'bg-[#1E3A8A] hover:bg-[#172554] text-white'
                        : 'bg-[#C2151F] hover:bg-[#A11019] text-white'}
                      onClick={confirmDeleteKpi}>
                      {delBusy ? 'Working…'
                        : delImpact?.will_retire ? 'Retire this KPI' : 'Delete'}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <TabsContent value="templates" className="mt-4">
              {/* Searchable KPI picker. Replaces a 222-option dropdown that had
                  to be scrolled; the selection rule is unchanged — a row still
                  points at a master KPI by key and nothing is ever typed. */}
              {pickerRow !== null && (
                <div className="fixed inset-0 z-50 bg-black/30 flex items-start justify-center pt-16 px-4"
                  onClick={() => setPickerRow(null)}>
                  <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl flex flex-col max-h-[75vh]"
                    data-testid="kpi-picker-panel" onClick={(e) => e.stopPropagation()}>
                    <div className="p-4 border-b border-[#F2F4F8]">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-[#172554]">Choose a KPI from the master</h3>
                        <button onClick={() => setPickerRow(null)}
                          className="text-[#98A1B2] hover:text-[#11182B] text-lg leading-none">×</button>
                      </div>
                      <Input autoFocus value={pq} onChange={(e) => setPq(e.target.value)}
                        data-testid="kpi-picker-search"
                        placeholder="Search by KPI name or key… e.g. machine, breakdown, mttr"
                        className="text-sm mb-2" />
                      <div className="flex flex-wrap gap-2">
                        <select value={pDept} onChange={(e) => setPDept(e.target.value)}
                          data-testid="kpi-picker-dept"
                          className="border border-[#E6E9F0] rounded-lg px-2 py-1 text-xs text-[#5A6377] max-w-[170px]">
                          <option value="">All departments</option>
                          {pickerDepts.map((d) => <option key={d} value={d}>{d}</option>)}
                        </select>
                        <select value={pRole} onChange={(e) => setPRole(e.target.value)}
                          data-testid="kpi-picker-role"
                          className="border border-[#E6E9F0] rounded-lg px-2 py-1 text-xs text-[#5A6377] max-w-[190px]">
                          <option value="">All roles</option>
                          {pickerRoles.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                        <select value={pSrc} onChange={(e) => setPSrc(e.target.value)}
                          data-testid="kpi-picker-source"
                          className="border border-[#E6E9F0] rounded-lg px-2 py-1 text-xs text-[#5A6377]">
                          <option value="">All software</option>
                          {pickerSources.map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
                        </select>
                        <select value={pMode} onChange={(e) => setPMode(e.target.value)}
                          data-testid="kpi-picker-mode"
                          className="border border-[#E6E9F0] rounded-lg px-2 py-1 text-xs text-[#5A6377]">
                          <option value="">Automatic + manual</option>
                          <option value="auto">Automatic only</option>
                          <option value="manual">Manual only</option>
                        </select>
                        <span className="ml-auto text-[11px] text-[#98A1B2] self-center"
                          data-testid="kpi-picker-count">
                          {pickerResults.length} of {masterAll.length}
                          {/* Why any match is unavailable, said before you
                              scroll rather than discovered at the bottom. */}
                          {(() => {
                            const taken = pickerResults.filter((m) => takenKeys.has(m.key)).length;
                            const ret = pickerResults.filter((m) => m.active === false).length;
                            return (taken ? ` · ${taken} already on this sheet` : '')
                                 + (ret ? ` · ${ret} retired` : '');
                          })()}
                        </span>
                      </div>
                    </div>
                    {/* Scrolls inside itself — the page never grows. */}
                    <div className="overflow-y-auto flex-1" data-testid="kpi-picker-results">
                      {pickerResults.length === 0 ? (
                        <p className="p-6 text-center text-sm text-[#98A1B2]">
                          No KPI matches. The master has {masterFlat.length} KPIs — try a shorter word,
                          or clear a filter.
                        </p>
                      ) : pickerResults.map((m) => {
                        // Already on THIS sheet. Shown rather than hidden: a KPI
                        // that silently vanished from the list reads as missing
                        // from the master, and the next move would be to create
                        // a second copy of it. Saying why it cannot be picked
                        // answers the question instead of raising it.
                        const taken = takenKeys.has(m.key);
                        // Withdrawn in KPI Master. Shown so it is findable —
                        // it is still in the master, so looking for it here is
                        // reasonable — but not pickable, because the save
                        // endpoint refuses it and a row that cannot be saved is
                        // worse than one that was never offered.
                        const retired = m.active === false;
                        const off = taken || retired;
                        return (
                        <button key={m.key} onClick={() => choosePickerKpi(m.key)}
                          disabled={off}
                          title={taken ? `${m.name} is already a row on this sheet.`
                            : retired ? `${m.name} is retired in KPI Master. `
                              + 'Re-activate it there, or use the KPI that replaced it.'
                            : undefined}
                          data-testid={`kpi-picker-opt-${m.key}`}
                          className={`w-full text-left px-4 py-2 border-b border-[#F7F8FB] flex items-baseline gap-2 ${
                            off ? 'bg-[#FAFBFD] cursor-not-allowed' : 'hover:bg-[#F5F8FF]'}`}>
                          <span className="flex-1 min-w-0">
                            <span className={`text-[13px] ${off ? 'text-[#98A1B2]' : 'text-[#11182B]'}`}>
                              {m.name}
                            </span>
                            <span className="ml-2 text-[10px] font-mono text-[#98A1B2]">{m.key}</span>
                            {retired && (
                              <span className="ml-2 text-[10px] font-semibold rounded px-1.5 py-0.5
                                               text-[#B91C1C] bg-[#FDE7E9]"
                                data-testid={`kpi-picker-retired-${m.key}`}>
                                retired in KPI Master
                              </span>
                            )}
                            {taken && (
                              <span className="ml-2 text-[10px] font-semibold rounded px-1.5 py-0.5
                                               text-[#8A5A12] bg-[#FBF7EC]"
                                data-testid={`kpi-picker-taken-${m.key}`}>
                                already on this sheet
                              </span>
                            )}
                          </span>
                          <span className="text-[11px] text-[#5A6377] w-28 shrink-0 truncate">{m.department || '—'}</span>
                          <span className="text-[11px] text-[#5A6377] w-32 shrink-0 truncate">{m.sourceLabel}</span>
                          <span className={`text-[10px] font-semibold shrink-0 rounded px-1.5 py-0.5 ${
                            off ? 'text-[#B4B9CC] bg-[#F4F6FB]'
                              : kpiMode(m) === 'auto' ? 'text-[#2563EB] bg-[#E8EFFD]'
                              : 'text-[#8A5A12] bg-[#FBF7EC]'}`}>
                            {kpiMode(m) === 'auto' ? 'AUTO' : 'MANUAL'}
                          </span>
                        </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
              <div className="tmpl-shell grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4 items-start">
                {/* ---- sidebar: scannable template list, grouped by department ---- */}
                <div className="tmpl-sidebar p-3.5">
                  {/* No "Seed defaults" button: the default role sheets, the
                      org-role sheets and everyone's assignments are all created
                      automatically at startup, so there is nothing to press. */}
                  <div className="flex items-center justify-between mb-2.5 px-0.5">
                    <h3 className="text-[13px] font-semibold text-[#11182B]">Templates</h3>
                    <span className="text-[11px] text-[#98A1B2]">{templates.length}</span>
                  </div>
                  {/* 65 sheets is too many to scan. Matches on the whole name, so
                      "plant head", "accounts" or "engineer" all find their group. */}
                  <div className="relative mb-2.5">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#B4B9CC]" />
                    <Input value={tq} onChange={(e) => setTq(e.target.value)}
                      data-testid="template-search"
                      placeholder="Search template…"
                      className="pl-8 text-sm h-9" />
                  </div>
                  {/* Stacked (below lg) the sidebar sits ABOVE the editor, so a
                      full-height list buries the sheet you just clicked under
                      seventy rows. Capped to about a third of the viewport
                      there; unchanged on desktop, where it sits alongside. */}
                  {/* One active sheet per role is an invariant, so a breach is
                      said out loud HERE rather than left in a server log. Two
                      sheets for one role look exactly like two legitimate
                      sheets — that is how the Design & NPD duplicate survived
                      for weeks. */}
                  {/* Sheets whose role no longer exists on anybody. Counted so
                      the number is visible without turning them on — and
                      phrased as the org chart, because that is what defines a
                      role: the designations employees actually hold. */}
                  {!!deadRoles.size && (
                    <button onClick={() => setShowDeadRoles((v) => !v)}
                      data-testid="tmpl-show-dead-roles"
                      aria-pressed={showDeadRoles}
                      title={showDeadRoles
                        ? 'Hide sheets whose role nobody holds'
                        : 'Show sheets named for a role no employee or open vacancy '
                          + 'has. They cannot be allocated to anybody.'}
                      className={`mb-2 w-full text-left text-[11px] rounded-[8px] px-2.5 py-1.5 border
                        transition-colors ${showDeadRoles
                          ? 'bg-[#5A6377] border-[#5A6377] text-white'
                          : 'bg-white border-[#E6E9F0] text-[#5A6377] hover:border-[#5A6377]'}`}>
                      {showDeadRoles
                        ? `hiding ${deadRoles.size} with no role`
                        : `${deadRoles.size} sheet(s) with no role`}
                    </button>
                  )}
                  {tmplHealth && !tmplHealth.healthy && (
                    <div className="mb-2 rounded-[8px] border border-[#F3C9C9] bg-[#FDECEC] px-2.5 py-2"
                      data-testid="tmpl-duplicate-warning">
                      <div className="text-[11.5px] font-semibold text-[#B91C1C]">
                        {tmplHealth.duplicate_logical_identities} role(s) have more than one active sheet
                      </div>
                      {(tmplHealth.duplicates || []).slice(0, 4).map((d) => (
                        <div key={`${d.department_key}-${d.designation_key}`}
                          className="text-[10.5px] text-[#7F1D1D] mt-0.5">
                          {d.templates.join('  ·  ')}
                        </div>
                      ))}
                      <div className="text-[10.5px] text-[#98A1B2] mt-1">
                        Same department and designation, different display names. Reconcile
                        to merge them — nothing is deleted.
                      </div>
                      {/* The banner used to NAME the cure and offer no way to
                          take it, so the only way to clear it was to know the
                          endpoint existed. It also re-checks afterwards: the
                          boot alignment retires duplicates on its own, so this
                          warning is often already out of date by the time it is
                          read, and a stale one is indistinguishable from a real
                          one until something asks the server again. */}
                      <button onClick={reconcileTemplates} disabled={busy}
                        data-testid="tmpl-reconcile"
                        className="mt-1.5 text-[10.5px] font-semibold text-[#B91C1C] underline
                                   hover:opacity-70 disabled:opacity-40">
                        {busy ? 'Checking…' : 'Reconcile now — nothing is deleted'}
                      </button>
                    </div>
                  )}
                  <div className="space-y-0.5 max-h-[34vh] lg:max-h-[60vh] overflow-y-auto pr-0.5">
                    {/* Grouped by department, with a separator per group. The
                        department comes from the backend (derived from who is
                        actually on each sheet), so no department name is
                        hardcoded here. */}
                    {(() => {
                      const raw = tmplGroups.length ? tmplGroups
                        : [{ department: '', templates: templates.map((t) => ({ name: t.name, count: (t.items || []).length })) }];
                      // Roles nobody holds, out of the way unless asked for.
                      const base = showDeadRoles ? raw : raw
                        .map((g) => ({ ...g,
                          templates: (g.templates || []).filter((t) => !deadRoles.has(t.name)) }))
                        .filter((g) => (g.templates || []).length);
                      const q = tq.trim().toLowerCase();
                      const groups = !q ? base : base
                        .map((g) => ({
                          ...g,
                          // A department match keeps the whole group, so typing
                          // "accounts" shows every Accounts sheet.
                          templates: (g.templates || []).filter((t) =>
                            t.name.toLowerCase().includes(q)
                            || (g.department || '').toLowerCase().includes(q)),
                        }))
                        .filter((g) => (g.templates || []).length);
                      if (q && !groups.length) {
                        return <p className="text-[12px] text-[#98A1B2] px-2 py-3">No template matches “{tq}”.</p>;
                      }
                      return groups.map((g, gi) => (
                      <div key={g.department || gi} className={gi ? 'mt-2.5 pt-2.5 border-t border-[#F0F1F6]' : ''}>
                        {g.department && (
                          <div className="tmpl-sidebar-group px-2.5 pb-1">
                            {g.department}
                            <span className="ml-1 font-normal normal-case text-[#B4B9CC]">({g.templates.length})</span>
                          </div>
                        )}
                        {g.templates.map((t) => (
                          /* Name on its own line, count underneath. Side by side
                             they fought for 258px and the name always lost —
                             "Accounts · Assistant Manager" rendered as
                             "Accounts · Assista…", which is unreadable in a list
                             whose whole job is telling two sheets apart. */
                          <button key={t.name} onClick={() => pickTemplate(t.name)}
                            data-testid={`kpi-template-${t.name}`}
                            aria-current={sel === t.name ? 'true' : undefined}
                            className={`tmpl-sidebar-item ${sel === t.name ? 'active' : ''}`}>
                            <span className="tmpl-sidebar-name">{t.name}</span>
                            {/* An empty sheet measures nothing, so it is called out
                                rather than shown as a quiet "(0)". */}
                            {t.empty ? (
                              <span className="tmpl-sidebar-meta text-[#DC2626] font-semibold">no KPIs</span>
                            ) : (
                              /* How the sheet is scored, at a glance: A
                                 automatic, M manual. Without it an all-manual
                                 sheet looks identical to a fully wired one.

                                 There is no third state. A row either names a
                                 KPI the master holds — and is then automatic or
                                 manual by that KPI's own source — or it cannot
                                 be saved at all: `save_kpi_template` refuses
                                 every row without a `kpi_key`. The "?" badge
                                 described a shape the app no longer accepts, so
                                 it only ever reported on data from before that
                                 rule, of which there is none left. */
                              <span className="tmpl-sidebar-meta tabular-nums">
                                {t.count} KPI{t.count === 1 ? '' : 's'}
                                {t.auto > 0 && <span className="text-[#2563EB] font-semibold"> {t.auto}A</span>}
                                {t.manual > 0 && <span className="text-[#B45309] font-semibold"> {t.manual}M</span>}
                                {' · 100%'}
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                      ));
                    })()}
                  </div>
                  <div className="mt-2.5 pt-2.5 border-t border-[#F0F1F6] text-[10px] text-[#9699AD] leading-relaxed">
                    <span className="text-[#2563EB] font-semibold">A</span>utomatic ·{' '}
                    <span className="text-[#B45309] font-semibold">M</span>anual
                  </div>
                  <button onClick={() => { setSel(''); setItems([]); setTname(''); }}
                    data-testid="kpi-new-template"
                    className="mt-2.5 w-full text-left text-xs font-medium text-[#4F46E5] hover:text-[#3730A3] px-2.5 py-1.5 rounded-lg hover:bg-[#F5F5FE]">
                    + New template
                  </button>
                </div>

                {/* ---- editor: header/actions, info banner, KPI rows ---- */}
                <div className="bg-white border border-[#E6E9F0] rounded-xl p-4 sm:p-5 min-w-0">
                  <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
                    <div className="min-w-0">
                      {/* The sheet name IS the role it belongs to. It used to be
                          a free-text box, and editing "Production · Manager" to
                          "Production · Manage" did not rename anything — the save
                          upserts on the name, so it CREATED a second sheet and
                          left the first behind. The new one matched no role, so
                          nobody could ever be scored on it.

                          Existing sheets therefore show their name read-only, and
                          a new one is CHOSEN from the roles the org chart has. A
                          position that does not exist yet is added to the org
                          chart, not typed in here. */}
                      {sel ? (
                        <div data-testid="tmpl-name-fixed">
                          {selDept && <div className="text-[11px] font-medium text-[#8B8FA8] uppercase tracking-wide mb-0.5">{selDept}{'\u00A0'}</div>}
                          <h2 className="text-lg font-bold text-[#172554] leading-tight"
                            title="A sheet is named after its role. To cover a new position, add it to the org chart and its sheet can be created here.">
                            {tname}
                          </h2>
                        </div>
                      ) : (() => {
                        const free = (orgRoles || []).filter(
                          (r) => r.template_name && !templates.some((t) => t.name === r.template_name));
                        if (orgRoles === null) {
                          return <span className="text-sm text-[#98A1B2]">Reading the org chart…</span>;
                        }
                        // Every role already covered is the normal, healthy state —
                        // an empty dropdown with no explanation reads as broken.
                        if (!free.length) {
                          return (
                            <span className="text-sm text-[#15654B] bg-[#EAF7F0] border border-[#CDEBDB] rounded-lg px-3 py-1.5"
                              data-testid="tmpl-name-none-left">
                              Every role in the org chart already has a sheet — nothing left to create.
                              <span className="text-[#5A6377]"> Add a position to the org chart to make one.</span>
                            </span>
                          );
                        }
                        return (
                          <select value={tname} onChange={(e) => setTname(e.target.value)}
                            data-testid="tmpl-name-picker"
                            className="border border-[#E6E9F0] rounded-lg px-3 py-1.5 text-sm max-w-md bg-white">
                            <option value="">Choose the role this sheet is for…</option>
                            {free.map((r) => (
                              <option key={r.template_name} value={r.template_name}>
                                {r.template_name}
                                {r.employees ? ` — ${r.employees} employee(s)` : ' — vacancy only'}
                              </option>
                            ))}
                          </select>
                        );
                      })()}
                      {/* A statement of fact, not a warning. The share is derived
                          from the KPI count, so a sheet with any rows on it
                          always totals 100 — there is no gap to fill and nothing
                          to "spread evenly", which is why that button is gone
                          too. An empty sheet IS worth saying: nothing on it can
                          be scored. */}
                      <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                        {items.length ? (
                          <span className="tmpl-weight-pill tmpl-weight-ok" data-testid="tmpl-weight-note">
                            {items.length} KPI{items.length === 1 ? '' : 's'} · {pct(100 / items.length)} each · Total 100% ✓
                          </span>
                        ) : (
                          <span className="tmpl-weight-pill tmpl-weight-empty" data-testid="tmpl-weight-note">
                            No KPIs on this sheet — nothing can be scored
                          </span>
                        )}
                        {dirty && (
                          <span className="tmpl-weight-pill tmpl-weight-warn">Unsaved changes</span>
                        )}
                        {/* Rows the KPI Master no longer backs, counted where
                            the sheet is summarised rather than left to be found
                            by scrolling 15 rows. */}
                        {(() => {
                          const bad = items.filter((i) => i.kpi_key && i.in_master === false).length;
                          const old = items.filter((i) => i.master_retired).length;
                          if (!bad && !old) return null;
                          return (
                            <span className="tmpl-weight-pill tmpl-weight-warn"
                              data-testid="tmpl-master-gap"
                              title={'A sheet can only score KPIs that exist in KPI Master. '
                                   + 'Replace these rows with the KPI that superseded them, or remove them.'}>
                              {bad ? `${bad} not in KPI Master` : ''}
                              {bad && old ? ' · ' : ''}
                              {old ? `${old} retired` : ''}
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                    {/* Actions, ordered by consequence rather than by history.
                        Save is the only filled control, the three that change
                        nothing on their own sit together as outlines, and Delete
                        is pushed past a divider so it is never the button next
                        to the one you meant. */}
                    <div className="tmpl-actions flex gap-2 flex-wrap items-center">
                      {sel && /plant head|general manager/i.test(sel) && (
                        <Button size="sm" variant="outline" onClick={addPlantHeadRows} disabled={busy}
                          data-testid="kpi-add-plant-head-rows"
                          title="Add the missing Plant Head KPI rows (weight 0 — existing rows and weights untouched)">
                          + Plant Head rows
                        </Button>
                      )}
                      {sel && (
                        <>
                          <Button size="sm" variant="outline" onClick={duplicateTemplate} disabled={busy}
                            data-testid="kpi-duplicate-template"
                            title="Create a new template with the same KPI rows. The original is untouched.">
                            <Copy className="h-3.5 w-3.5" /> Duplicate
                          </Button>
                          <Button size="sm" variant="outline" onClick={copySelectedKpis} disabled={busy || !rowSel.size}
                            data-testid="kpi-copy-kpis"
                            title="Tick rows below, then copy them into another template">
                            <Copy className="h-3.5 w-3.5" /> Copy {rowSel.size || ''} KPI{rowSel.size === 1 ? '' : 's'}
                          </Button>
                          <Button size="sm" variant="outline" onClick={viewEmployees} disabled={busy}
                            data-testid="kpi-view-employees">
                            <Users className="h-3.5 w-3.5" /> View employees
                          </Button>
                        </>
                      )}
                      <Button size="sm" onClick={saveTemplate} disabled={busy}
                        data-testid="kpi-save-template" className="tmpl-save-btn">
                        <Save className="h-3.5 w-3.5" /> {dirty ? 'Save changes' : 'Save'}
                      </Button>
                      {sel && (
                        <span className="tmpl-action-sep" aria-hidden />
                      )}
                      {sel && (
                        <Button size="sm" variant="destructive" onClick={deleteTemplate} disabled={busy}
                          data-testid="kpi-delete-template" className="tmpl-delete-btn">
                          <Trash2 className="h-3.5 w-3.5" /> Delete
                        </Button>
                      )}
                    </div>
                  </div>

                  {tmplEmps && (
                    <div className="mt-3 p-3 rounded-lg bg-[#F7F8FB] border border-[#EEF1F6]">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[13px] font-semibold text-[#172554]">
                          {tmplEmps.count} employee{tmplEmps.count === 1 ? '' : 's'} on {tmplEmps.template}
                        </span>
                        <button onClick={() => setTmplEmps(null)} className="text-[11px] text-[#5A6377] hover:underline">close</button>
                      </div>
                      {tmplEmps.count === 0 ? (
                        <p className="text-[12px] text-[#98A1B2]">Nobody is on this template yet.</p>
                      ) : (
                        <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
                          {tmplEmps.employees.map((e) => (
                            <Link key={e.id} to={`/employees/${e.id}?tab=kpi`}
                              className="text-[12px] bg-white border border-[#E6E9F0] rounded-full px-2.5 py-1 hover:border-[#2563EB] hover:text-[#2563EB]">
                              {e.name} <span className="text-[#98A1B2]">· {e.unit}</span>
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Compact, collapsible explainer — the information still
                      exists, it just no longer dominates the top of the page. */}
                  {sel && (
                    <button onClick={() => setTmplInfoOpen((v) => !v)}
                      data-testid="tmpl-info-toggle"
                      className="tmpl-info-banner mt-3 w-full flex items-center gap-2 px-3 py-1.5 text-left">
                      <Info className="h-3.5 w-3.5 text-[#8B5CF6] shrink-0" />
                      <span className="text-[11px] text-[#6B6F8A] flex-1">
                        {tmplInfoOpen
                          ? 'This is the global sheet — the same KPIs, objectives, criteria and weights for every employee on it, in every unit. Monthly actuals and marks live on each employee\'s own KPI tab.'
                          : 'About this sheet — same for every employee on it, every unit.'}
                      </span>
                      {tmplInfoOpen ? <ChevronDown className="h-3.5 w-3.5 text-[#8B8FA8]" /> : <ChevronRight className="h-3.5 w-3.5 text-[#8B8FA8]" />}
                    </button>
                  )}

                  {/* ---- KPI rows: hybrid table + subtle card treatment ---- */}
                  {/* The Objective column is dropped below 1536px and reappears
                      under the KPI name instead. It used to hide at `lg`, which
                      is a VIEWPORT width — on a 1280px screen the column stayed
                      on while the editor itself was only 602px wide, so the
                      table ran 842px and cut off Scoring rule and Weight: the
                      two things the page exists to show. Dropping one column
                      rather than scrolling keeps both in view. */}
                  <div className="mt-3 overflow-x-auto">
                    <table className="w-full text-sm min-w-[560px]">
                      <thead className="text-[#9699AD] text-[11px] uppercase tracking-wide">
                        <tr>
                          <th scope="col" className="py-2 px-2 w-7">
                            <input type="checkbox" data-testid="kpi-rows-select-all"
                              aria-label="Select all KPI rows"
                              checked={items.length > 0 && rowSel.size === items.length}
                              onChange={() => setRowSel(rowSel.size === items.length
                                ? new Set() : new Set(items.map((_, i) => i)))}
                              title="Select all rows" />
                          </th>
                          <th scope="col" className="py-2 px-1 w-9 text-left">#</th>
                          <th scope="col" className="py-2 px-2 text-left w-40">KPI</th>
                          <th scope="col" className="py-2 px-2 text-left hidden 2xl:table-cell">Objective</th>
                          {/* Header mirrors the ladder's own two columns, so the
                              bare number in each rung is never ambiguous. */}
                          <th scope="col" className="py-2 px-2 text-left">
                            <span className="tmpl-ladder-row">
                              <span className="tmpl-mark font-semibold text-[#9699AD]">Marks</span>
                              <span>Scoring rule</span>
                            </span>
                          </th>
                          <th scope="col" className="py-2 px-2 w-14 text-right"
                            title="Worked out from how many KPIs are on the sheet — every one carries an equal share of 100%">
                            Weight
                          </th>
                          <th scope="col" className="py-2 px-1 w-7"><span className="sr-only">Remove</span></th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((it, idx) => {
                          const m = masterFlat.find((x) => x.key === it.kpi_key);
                          const hasBands = m && Array.isArray(m.bands) && m.bands.length > 0;
                          const dx = m;          // the same master record the row is linked to
                          // What the strip should actually say. Four distinct
                          // situations that were previously collapsed into one
                          // red "NO DATA IN SOURCE", which was wrong for three
                          // of them:
                          //   • not used in this unit  -> nothing was computed
                          //   • typed in each month    -> no source by design
                          //   • source has no field    -> a real data problem
                          //   • scored                 -> working
                          const state = (() => {
                            if (!dx) return {
                              chip: 'NOT IN KPI MASTER',
                              cls: 'bg-[#FDE7E9] text-[#A3161F]',
                              detail: 'This row points at no KPI in the master, so nothing can score it.',
                              detailCls: 'text-[#A3161F]',
                            };
                            const hasLadder = Array.isArray(dx.bands) && dx.bands.length > 0;
                            if (dx.mode !== 'auto' || !dx.field) return {
                              chip: 'TYPED IN EACH MONTH',
                              cls: 'bg-[#FBF2E3] text-[#8A5A12]',
                              detail: hasLadder ? null : 'No marking ladder yet — this row cannot award a mark.',
                              detailCls: 'text-[#A3161F]',
                            };
                            return {
                              chip: 'READS FROM SOFTWARE',
                              cls: 'bg-[#E4F6EE] text-[#15654B]',
                              // `pending` is the master's own note about what the
                              // source does not expose yet. Shown as information,
                              // not as a failure of this sheet.
                              detail: !hasLadder
                                ? 'No marking ladder yet — this row cannot award a mark.'
                                : (dx.pending || null),
                              detailCls: !hasLadder ? 'text-[#A3161F]' : 'text-[#6B7186]',
                            };
                          })();
                          return (
                          <React.Fragment key={idx}>
                          <tr
                            draggable
                            data-testid={`kpi-row-${idx}`}
                            onDragStart={() => setDragIdx(idx)}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={() => { if (dragIdx !== null) moveRow(dragIdx, idx); setDragIdx(null); }}
                            onDragEnd={() => setDragIdx(null)}
                            className={`tmpl-row border-b border-[#F2F4F8] align-top ${
                              dragIdx === idx ? 'opacity-40' : ''} ${rowSel.has(idx) ? 'bg-[#F5F8FF]' : ''}`}>
                            <td className="py-2 px-2 align-top">
                              <input type="checkbox" checked={rowSel.has(idx)}
                                data-testid={`kpi-row-pick-${idx}`}
                                aria-label={`Select ${it.kpi_key || it.kpi || `row ${idx + 1}`}`}
                                onChange={() => setRowSel((prev) => {
                                  const n = new Set(prev);
                                  n.has(idx) ? n.delete(idx) : n.add(idx);
                                  return n;
                                })} />
                            </td>
                            <td className="py-2 px-1 text-[#B4B9CC] whitespace-nowrap align-top">
                              {/* Drag handle for mouse; the arrows are the fallback
                                  that works on touch screens (§28). */}
                              <span className="cursor-grab select-none" title="Drag to reorder">☰</span>{' '}
                              {idx + 1}
                              <div className="leading-none mt-0.5">
                                <button onClick={() => moveRow(idx, idx - 1)} disabled={idx === 0}
                                  data-testid={`kpi-row-up-${idx}`} aria-label={`Move row ${idx + 1} up`}
                                  className="text-[10px] px-0.5 text-[#9699AD] hover:text-[#4F46E5] disabled:opacity-25"
                                  title="Move up">▲</button>
                                <button onClick={() => moveRow(idx, idx + 1)} disabled={idx === items.length - 1}
                                  data-testid={`kpi-row-down-${idx}`} aria-label={`Move row ${idx + 1} down`}
                                  className="text-[10px] px-0.5 text-[#9699AD] hover:text-[#4F46E5] disabled:opacity-25"
                                  title="Move down">▼</button>
                              </div>
                            </td>
                            {/* Width is pinned on a wrapper, not the cell. An
                                auto-layout table treats `w-40` on a <td> as a
                                suggestion and lets the longest word win, which
                                is how this column reached 301px off the back of
                                one long `kpi_key`. */}
                            {/* Identity: what this KPI IS. Name, its key, and
                                who supplies the number — the last of which used
                                to sit inside the scoring box, where it read as a
                                property of the ladder rather than of the KPI. */}
                            <td className="py-2 px-2 align-top">
                              <div className="w-[200px] 2xl:w-[228px] max-w-full">
                              {/* Pick from the master — never typed. */}
                              <button onClick={() => openPicker(idx)}
                                data-testid={`kpi-row-picker-${idx}`}
                                title={(it.kpi_key
                                  ? (masterFlat.find((x) => x.key === it.kpi_key)?.name || it.kpi)
                                  : '') || 'Search the KPI master'}
                                className="tmpl-kpi-btn">
                                <span className="flex-1 min-w-0">
                                  {it.kpi_key
                                    ? (masterFlat.find((x) => x.key === it.kpi_key)?.name || it.kpi)
                                    : <span className="text-[#98A1B2] font-normal">Search the KPI master…</span>}
                                </span>
                                <Search className="h-3 w-3 text-[#8E95AC] tmpl-kpi-mag" aria-hidden />
                              </button>
                              {it.kpi_key ? (
                                <div className="tmpl-kpi-key mt-0.5" title={it.kpi_key}>{it.kpi_key}</div>
                              ) : it.kpi ? (
                                <div className="text-[10.5px] text-[#DC2626] mt-0.5">unlinked: {it.kpi}</div>
                              ) : null}
                              {/* The master's verdict on this row's KPI. A sheet
                                  can only score what KPI Master still holds, so
                                  a row naming a withdrawn KPI earns nothing —
                                  and looked exactly like a working row, which is
                                  how 24 of them sat on Management sheets. */}
                              {it.kpi_key && it.in_master === false && (
                                <div className="text-[10.5px] text-[#DC2626] mt-0.5"
                                  data-testid={`kpi-row-not-in-master-${idx}`}
                                  title="A template can only use KPIs that exist in KPI Master. Create it there, or remove this row.">
                                  not in KPI Master — cannot be scored
                                </div>
                              )}
                              {it.master_retired && (
                                <div className="text-[10.5px] text-[#B14A0E] mt-0.5"
                                  data-testid={`kpi-row-retired-${idx}`}
                                  title="Retired in KPI Master. The row is kept so settled months are unchanged, but it earns no new marks. Replace it with the KPI that superseded it.">
                                  retired in KPI Master — no new marks
                                </div>
                              )}
                              {/* A template is ONE sheet shared by every plant,
                                  so the criteria on it is the shared ladder's.
                                  Without this line a reader on Unit 3 sees that
                                  sentence and concludes Unit 3 is showing Unit
                                  1's ladder — the sheet simply has one. Said
                                  here so the override is visible where the
                                  confusion happens. */}
                              {it.plants_with_own_ladder?.length > 0 && (
                                <div className="text-[10.5px] text-[#2563EB] mt-0.5"
                                  data-testid={`kpi-row-plant-ladders-${idx}`}
                                  title={`Marked by their own ladder, not the one shown here: ${it.plants_with_own_ladder.join(', ')}. Edit a plant's ladder in KPI Master → the KPI → Applies to.`}>
                                  ● {it.plants_with_own_ladder.length === 1
                                      ? `${it.plants_with_own_ladder[0]} has its own ladder`
                                      : `${it.plants_with_own_ladder.length} plants have their own ladder`}
                                </div>
                              )}
                              {m && (
                                <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                                  <span className={`tmpl-src ${m.sourceKey === 'manual' ? 'tmpl-src-manual' : 'tmpl-src-auto'}`}>
                                    {m.sourceKey === 'manual' ? 'Manual' : m.sourceLabel}
                                  </span>
                                  {!m.field && m.sourceKey !== 'manual' && (
                                    <span className="text-[10px] text-[#DC2626]">no source field yet</span>
                                  )}
                                </div>
                              )}
                              {/* The objective, when its own column has been
                                  dropped for width. Same text, same source —
                                  it moves rather than disappearing. */}
                              {it.objective && (
                                <div className="2xl:hidden mt-1.5 flex items-start gap-1.5 text-[11.5px]
                                                leading-snug text-[#6B7186]">
                                  <Target className="h-3 w-3 text-[#93A3C9] mt-[2px] shrink-0" />
                                  <span className="tmpl-objective-clamp">{it.objective}</span>
                                </div>
                              )}
                              </div>
                            </td>
                            <td className="py-2 px-2 hidden 2xl:table-cell align-top">
                              {/* Objective & criteria belong to the master, so the
                                  same KPI reads identically on every role's sheet. */}
                              <div className="flex items-start gap-1.5 text-[12px] leading-snug text-[#5A6377] min-w-[140px] max-w-[240px]">
                                {it.objective
                                  ? <><Target className="h-3 w-3 text-[#93A3C9] mt-[3px] shrink-0" /><span>{it.objective}</span></>
                                  : <span className="text-[#C9CFDC]">—</span>}
                              </div>
                            </td>
                            {/* The ladder. Marks in a fixed leading column so
                                every rung lines up down the table — the scale is
                                then readable at a glance instead of five
                                sentences that happen to start with a number. */}
                            <td className="py-2 px-2 align-top">
                              <div className="max-w-[300px] 2xl:max-w-[340px]">
                                {hasBands ? (
                                  <div className="tmpl-ladder">
                                    {bandRows(m.bands).map((b, bi) => (
                                      <div key={bi} className="tmpl-ladder-row">
                                        <span className={`tmpl-mark ${Number(b.score) < 0 ? 'tmpl-mark-neg' : ''}`}>
                                          {b.score}
                                        </span>
                                        <span>{b.condition}</span>
                                      </div>
                                    ))}
                                  </div>
                                ) : it.criteria ? (
                                  splitCriteriaText(it.criteria).map((line, li) => (
                                    <div key={li} className="tmpl-ladder-text">{line}</div>
                                  ))
                                ) : (
                                  <span className="text-[11.5px] text-[#C9CFDC]">No scoring rule set</span>
                                )}
                                {m?.field && (
                                  <div className="mt-1 font-mono text-[9.5px] text-[#B4B9CC] break-all">{m.field}</div>
                                )}
                              </div>
                            </td>
                            {/* Derived and read-only. The engine works the
                                weighting out from the KPI COUNT — every row on
                                a sheet carries an equal share summing to 100
                                (`normalised_kpi_weights`) — and never reads the
                                stored number.

                                It used to be a box you could type into, which
                                was the worst possible arrangement: it looked
                                like it set how much a KPI was worth and did
                                nothing. On this very template the first 13 rows
                                said 7.7% and the three mandatory KPIs said 0%,
                                while all 17 were in fact worth 5.88% each. */}
                            <td className="py-2 px-2 text-right tabular-nums align-top"
                              data-testid={`tmpl-weight-${idx}`}
                              title={`Each of the ${items.length} KPIs on this sheet is worth the same share of 100%`}>
                              <span className="text-[13px] font-semibold text-[#172554]">
                                {pct(items.length ? 100 / items.length : 0)}
                              </span>
                            </td>
                            <td className="py-2 px-1 text-right align-top">
                              <button onClick={() => delItem(idx)} data-testid={`kpi-row-delete-${idx}`}
                                title="Remove this KPI from the sheet"
                                aria-label={`Remove ${it.kpi_key || it.kpi || `row ${idx + 1}`} from this sheet`}
                                className="tmpl-row-del text-[#C9CFDC] hover:text-[#DC2626] text-sm px-1">✕</button>
                            </td>
                          </tr>

                          {/* Why this KPI scores what it scores. Read from the
                              SAME engine the monthly run uses — never a second
                              opinion — so a zero can be traced to its cause
                              instead of being read as bad performance.

                              The case this exists for: Hold Material scored 0
                              not because nothing was held, but because the ops
                              export carries no hold-material field at all, so
                              the JSON had nothing to pick up. That is a data
                              problem and it has to say so. */}
                          {dx && state && (
                            <tr className="border-b border-[#F2F4F8] bg-[#FBFCFE]"
                                data-testid={`kpi-review-${idx}`}>
                              <td></td><td></td>
                              <td colSpan={5} className="py-2 px-2">
                                <div className="flex flex-wrap items-start gap-x-5 gap-y-1 text-[11.5px] leading-snug">
                                  <span className={`px-1.5 py-0.5 rounded font-semibold ${state.cls}`}>
                                    {state.chip}
                                  </span>
                                  {dx && dx.field && (
                                    <span className="text-[#6B7186]">
                                      reads <code className="text-[#2563EB]">{dx.field}</code>
                                      {dx.sourceLabel ? <span className="text-[#98A1B2]"> · {dx.sourceLabel}</span> : null}
                                    </span>
                                  )}
                                  {/* No value and no mark here on purpose. A template
                                      is global; a figure belongs to one unit and one
                                      month, and quoting one under a shared sheet is
                                      what made this strip contradict itself. The
                                      monthly numbers live on the employee's KPI tab. */}
                                  {dx && dx.uom && (
                                    <span className="text-[#6B7186]">measured in <b>{dx.uom}</b></span>
                                  )}
                                  {state.detail && (
                                    <span className={`basis-full ${state.detailCls}`}>{state.detail}</span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                          </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <button onClick={addItem} data-testid="kpi-add-row"
                    className="mt-2.5 text-xs font-medium text-[#4F46E5] hover:text-[#3730A3]">+ Add KPI row</button>
                </div>
              </div>
            </TabsContent>

            {/* Categories / Criteria panes removed with their triggers above.
                To bring either back: restore its <TabsTrigger> and the pane
                  <TabsContent value="categories"><CategoriesTab /></TabsContent>
                  <TabsContent value="criteria"><CriteriaTab /></TabsContent>
                and re-import the component at the top of this file. */}

            <TabsContent value="source" className="mt-4">
              <SourceData />
            </TabsContent>

            <TabsContent value="everyone" className="mt-4">
              <MandatoryKpis masterFlat={masterFlat}
                onChanged={async () => {
                  // The list it just changed is the templates list, so re-read
                  // it rather than leaving the other tab a version behind.
                  const t = await kpiApi.templates();
                  setTemplates(t.data || []);
                }} />
            </TabsContent>

            {/* REVIEW removed on request. The pane below is kept, unreachable,
                for the same reason the Categories and Criteria panes above
                were: restoring it is putting its <TabsTrigger> back, not
                rewriting it. It holds two things nothing else duplicates —
                the automation status, and the BULK manual-mark entry. Marks
                can still be entered per employee on their own KPI tab. */}
            <TabsContent value="review-removed" className="mt-4">
              {/* Automation. Kept in front of the manual list because this is now
                  where almost every mark comes from. */}
              <div className="bg-white border border-[#E6E9F0] rounded-xl p-5 mb-4">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <h2 className="font-display text-lg font-bold tracking-[-0.015em] text-[#172554]">Automatic scoring</h2>
                    {/* Written for someone who has never seen this screen. The two
                        jobs behind it are a daily reading and a monthly posting,
                        so that is what they are called here. */}
                    <p className="text-[13px] text-[#5A6377] mt-0.5 max-w-3xl">
                      {sched ? (
                        <>
                          Marks that come from the plant software are worked out and put on
                          people's KPI sheets <b>automatically on day {sched.run_day} of every month</b>,
                          for the month just finished. Nothing needs to be clicked.
                        </>
                      ) : 'Loading…'}
                    </p>
                  </div>
                </div>

                {sched && (
                  <div className="mt-3 rounded-lg bg-[#F7F8FB] border border-[#EEF1F6] p-3">
                    <p className="text-[13px] text-[#11182B] font-medium">
                      {sched.previous_month_published
                        ? <>{sched.previous_month.month} is done — its marks are on people's sheets and locked.</>
                        : sched.previous_month_blocked
                          ? <>{sched.previous_month.month} cannot be worked out.</>
                          : <>{sched.previous_month.month} has not been done yet.</>}
                    </p>
                    <p className="text-[12px] text-[#5A6377] mt-0.5">
                      {sched.previous_month_published ? (
                        <>Locked means the marks stay exactly as they were, even though the plant
                          software keeps changing.</>
                      ) : sched.previous_month_blocked ? (
                        <>
                          The nightly reading was not running during {sched.previous_month.month},
                          so its tool and inventory figures no longer exist anywhere — the plant
                          software cannot look them up afterwards. Doing it now would use
                          <b> today's</b> figures and lock them in as if they were
                          {' '}{sched.previous_month.month}'s. Every other KPI would be correct.
                        </>
                      ) : (
                        <>It will happen on its own on day {sched.run_day}. Use the button only if
                          you do not want to wait.</>
                      )}
                    </p>
                    <Button size="sm" className="mt-2"
                      variant={sched.previous_month_blocked ? 'outline' : 'default'}
                      disabled={busy || sched.previous_month_published}
                      onClick={publishMonth}>
                      {sched.previous_month_published
                        ? `${sched.previous_month.month} already done`
                        : sched.previous_month_blocked
                          ? `Work out ${sched.previous_month.month} anyway`
                          : `Work out ${sched.previous_month.month} now`}
                    </Button>
                  </div>
                )}

                {sched && (
                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-[#98A1B2] mb-1.5">
                        Daily reading — {sched.unit || 'this unit'}
                      </div>
                      {/* Two numbers in the plant software only ever show "right
                          now" and cannot be looked up for a past month, so the
                          software reads them once a night and keeps the reading. */}
                      <p className="text-[12px] text-[#5A6377] mb-1.5">
                        Tool PM status and negative inventory only ever show today's position —
                        the plant software cannot look them up for a finished month. So they are
                        read every night and kept. <b>The last reading of a month is that month's figure.</b>
                      </p>
                      {Object.keys(sched.captures || {}).length === 0 ? (
                        <p className="text-[13px] text-[#B14A0E]">
                          Nothing has been read yet for this unit. Until the first reading,
                          a finished month would fall back to today's numbers instead of its own.
                        </p>
                      ) : (
                        <div className="space-y-1">
                          {Object.entries(sched.captures).map(([m, us]) => (
                            <div key={m} className="text-[13px] text-[#5A6377]">
                              <span className="font-semibold text-[#11182B]">{m}</span>
                              {m === sched.current_month ? ' (still running)' : ''}{' — '}
                              {us.map((u) => `read on ${u.days_captured} day${u.days_captured === 1 ? '' : 's'}, last on the ${Number(u.last_day.slice(8))}${u.days_captured ? '' : ''}`).join('; ')}
                            </div>
                          ))}
                        </div>
                      )}
                      <button onClick={captureNow} disabled={busy}
                        className="mt-2 text-[11px] text-[#2563EB] hover:underline disabled:opacity-50">
                        Read them again now
                      </button>
                      <span className="text-[11px] text-[#98A1B2]"> — only needed if tonight's reading failed</span>
                    </div>
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-[#98A1B2] mb-1.5">Months already worked out</div>
                      {(sched.runs || []).length === 0 ? (
                        <p className="text-[13px] text-[#98A1B2]">None yet this financial year.</p>
                      ) : (
                        <div className="space-y-1">
                          {sched.runs.map((r) => (
                            <div key={r.month} className="text-[13px] text-[#5A6377]">
                              <span className="font-semibold text-[#11182B]">{r.month}</span>{' — '}
                              {r.written} marks put on {(r.sheets || []).length} sheet(s)
                              {r.frozen
                                ? <span className="ml-1.5 text-[10px] font-semibold text-[#15654B] bg-[#E7F7EF] rounded px-1.5 py-0.5">LOCKED</span>
                                : <span className="ml-1.5 text-[10px] font-semibold text-[#B14A0E] bg-[#FFF3E0] rounded px-1.5 py-0.5">NOT LOCKED</span>}
                              {(r.sheets_without_bands || []).length > 0 && (
                                <div className="text-[11px] text-[#B14A0E]"
                                  title={r.sheets_without_bands.map((x) => `${x.unit} · ${x.sheet}`).join('\n')}>
                                  {r.sheets_without_bands.length} sheet(s) had no scoring rule set, so scored nothing
                                </div>
                              )}
                              {(r.errors || []).length > 0 && (
                                <div className="text-[11px] text-[#C2151F]"
                                  title={r.errors.map((x) => `${x.unit} · ${x.sheet}: ${x.error}`).join('\n')}>
                                  {r.errors.length} sheet(s) could not be worked out
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Who would be asked for the manual marks, and what HR is left
                  holding. Read-only on purpose: asking people by email is OFF,
                  so this is the dry run you check before it is ever switched on. */}
              {(mkPrev || mkQueue) && (
                <div className="bg-white border border-[#E6E9F0] rounded-xl p-5 mb-4" data-testid="manual-kpi-hr-panel">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <h2 className="font-display text-lg font-bold tracking-[-0.015em] text-[#172554]">
                        Who fills the manual KPIs
                      </h2>
                      <p className="text-[13px] text-[#5A6377] mt-0.5 max-w-3xl">
                        Each KPI says who fills it — HR or the employee's reporting head — and that is
                        set on the KPI Master. Everything not set stays with HR. Employees are never
                        asked to fill their own.
                        {mkPrev && <> For <b>{mkPrev.month} {mkPrev.fy}</b>.</>}
                      </p>
                    </div>
                    <span className="text-[10px] font-semibold rounded px-2 py-1 bg-[#FFF3E0] text-[#B14A0E] whitespace-nowrap">
                      ASKING BY EMAIL IS OFF
                    </span>
                  </div>

                  <div className="mt-3 grid gap-3 sm:grid-cols-3">
                    {[['People who would be asked', mkPrev?.recipient_count ?? '—', '#172554'],
                      ['Employees covered', mkPrev?.employee_count ?? '—', '#172554'],
                      ['Left with HR', mkQueue?.employee_count ?? '—',
                       (mkQueue?.employee_count ? '#B14A0E' : '#15654B')]].map(([l, v, c]) => (
                      <div key={l} className="rounded-lg bg-[#F7F8FB] border border-[#EEF1F6] p-3">
                        <div className="text-[11px] text-[#5A6377]">{l}</div>
                        <div className="text-xl font-bold" style={{ color: c }}>{v}</div>
                      </div>
                    ))}
                  </div>

                  <button onClick={() => setMkOpen((v) => !v)}
                    className="text-[12px] text-[#2563EB] hover:underline mt-3"
                    data-testid="manual-kpi-hr-toggle">
                    {mkOpen ? '▾ hide the detail' : '▸ show who gets what'}
                  </button>

                  {mkOpen && (
                    <div className="mt-3 grid gap-4 lg:grid-cols-2">
                      <div>
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-[#98A1B2] mb-1.5">
                          Would be asked ({mkPrev?.recipient_count || 0})
                        </div>
                        {!(mkPrev?.recipients || []).length ? (
                          <p className="text-[13px] text-[#98A1B2]">Nobody — every manual KPI is still HR's.</p>
                        ) : (
                          <div className="space-y-1.5 max-h-72 overflow-y-auto">
                            {mkPrev.recipients.map((g) => (
                              <div key={g.responsible_id} className="border border-[#F2F4F8] rounded-lg px-3 py-2">
                                <div className="text-[13px] font-semibold text-[#11182B]">{g.name}</div>
                                <div className="text-[11px] text-[#98A1B2]">{g.email}</div>
                                <div className="text-[12px] text-[#5A6377] mt-0.5">
                                  {g.employees.length} employee(s) · {g.total_pending} KPI(s) — one request, not {g.employees.length}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-[#98A1B2] mb-1.5">
                          HR has to do these ({mkQueue?.total_pending || 0} KPIs)
                        </div>
                        {!(mkQueue?.employees || []).length ? (
                          <p className="text-[13px] text-[#98A1B2]">Nothing left with HR.</p>
                        ) : (
                          <div className="space-y-1.5 max-h-72 overflow-y-auto">
                            {mkQueue.employees.map((r) => (
                              <div key={r.employee_id} className="border border-[#F2F4F8] rounded-lg px-3 py-2">
                                <div className="text-[13px] font-semibold text-[#11182B]">
                                  {r.name} <span className="text-[11px] font-normal text-[#98A1B2]">· {r.pending} pending</span>
                                </div>
                                <div className="text-[11px] text-[#5A6377]">
                                  {r.responsibility === 'hr'
                                    ? 'This KPI is set to HR'
                                    : 'Nobody up the org chart has an email'}
                                </div>
                                {r.senior_chain !== '—' && (
                                  <div className="text-[11px] text-[#98A1B2]">Chain checked: {r.senior_chain}</div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="bg-white border border-[#E6E9F0] rounded-xl p-5">
                <h2 className="font-display text-lg font-bold tracking-[-0.015em] text-[#172554]">Manual KPIs</h2>
                <p className="text-[13px] text-[#5A6377] mt-0.5 mb-3">
                  KPIs no software measures. This list is for finding them — marks are entered on the
                  employee's own KPI tab, which is the only place a score is typed.
                </p>
                {!review ? <p className="text-[#98A1B2]">Loading…</p> : review.rows.length === 0 ? (
                  <p className="text-[#98A1B2] py-8 text-center">Nothing waiting to be scored by hand.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-[#EFF1F6] text-[#5A6377] text-xs uppercase">
                        <tr><th className="py-2 px-3 text-left">Employee</th><th className="py-2 px-3 text-left">Month</th><th className="py-2 px-3 text-left">KPI</th><th className="py-2 px-3 text-right">Self</th><th className="py-2 px-3 text-right">Mark</th><th className="py-2 px-3"></th></tr>
                      </thead>
                      <tbody>
                        {review.rows.map((r) => {
                          const key = `${r.employee_id}|${r.month}|${r.kpi_id}`;
                          return (
                            <tr key={key} className="border-b border-[#F2F4F8]">
                              <td className="py-2 px-3 text-[#11182B]">{r.employee_name}</td>
                              <td className="py-2 px-3 text-[#5A6377]">{r.month}</td>
                              <td className="py-2 px-3 text-[#5A6377]">
                                {r.kpi} <span className="text-[11px] text-[#98A1B2]">({r.weight}%)</span>
                                {r.status === 'auto_computed' && (
                                  <span className="ml-1 text-[10px] font-semibold text-[#2563EB] bg-[#E8EFFD] rounded px-1.5 py-0.5" title={r.auto_basis || ''}>AUTO</span>
                                )}
                                {r.status === 'auto_computed' && r.auto_basis && (
                                  <div className="text-[11px] text-[#98A1B2]">{r.auto_basis}</div>
                                )}
                              </td>
                              <td className="py-2 px-3 text-right text-[#8A5A12]">{r.self_score}</td>
                              <td className="py-2 px-3 text-right text-[#11182B] font-semibold">{r.self_score ?? '—'}</td>
                              {/* Display only. Scores are entered in one place —
                                  the employee's own KPI tab — so two screens can
                                  never hold different marks for the same month. */}
                              <td className="py-2 px-3 text-right">
                                <Link to={`/employees/${r.employee_id}?tab=kpi`}
                                  className="text-[13px] text-[#2563EB] hover:underline whitespace-nowrap">
                                  Open sheet →
                                </Link>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* ---------------- Auto KPI (KPI 2.0) ---------------- */}
          </Tabs>
        </div>
      </div>
    </div>
  );
}
